import logger from "./utils/logger";
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Home, FileText, Plus, ChevronRight, ChevronLeft, ChevronDown, Download, ArrowLeft, Check, CheckCircle, AlertCircle, AlertTriangle, Calendar, User, MapPin, Trash2, Eye, GitCompare, X, XCircle, Sparkles, Edit, UserCheck, UserPlus, Bell, Clock, MessageSquare } from 'lucide-react';
import Layout from './components/layout/Layout';
import ConfirmDialog from './components/ui/ConfirmDialog';
import UpgradeModal from './components/ui/UpgradeModal';
import AlertDialog from './components/ui/AlertDialog';
import PhotoModal from './components/ui/PhotoModal';
import CountrySelect from './components/forms/CountrySelect';
import LeaseForm from './components/leases/LeaseForm';
import LeaseCard from './components/leases/LeaseCard';
import { useProperties } from './hooks/useProperties';
import { useInspections } from './hooks/useInspections';
import { useLeases } from './hooks/useLeases';
import { useClarity } from './hooks/useClarity';
import { useGoogleAnalytics } from './hooks/useGoogleAnalytics';
import { supabase } from './services/supabase/client';
import { uploadPropertyPhoto, uploadPropertyDocument } from './services/supabase/propertyStorage';
import { getProperty } from './services/supabase/properties';
import { deleteInspection } from './services/supabase/inspections';
import { RENTAL_STATUS, CURRENCIES, getPropertyTypeLabel, formatPropertyAddress } from './utils/constants';
import { usePropertyPhotoUrl } from './hooks/usePropertyPhoto';
import { useAuth } from './contexts/AuthContext';
import { generateInspectionPDF, generateComparisonPDF } from './services/pdf/exportInspection';
import { canCreateProperty, canCreateInspection, canCreateInspectionForProperty, startProTrial, getSubscriptionLimits, canAddPhotoToItem } from './services/supabase/subscriptions';
import { createCheckoutSession, createPortalSession } from './services/stripe/checkout';
import { getTenantProperties } from './services/supabase/tenantInvitations';
import InviteTenantModal from './components/modals/InviteTenantModal';
import { usePropertyRole } from './hooks/usePropertyRole';
import { createTenantInspection } from './services/supabase/inspections';
import { trackPixelCustomEvent } from './utils/metaPixel';

// Helper function to get property ID from inspection (handles both formats)
const getPropertyId = (inspection) => inspection.property_id || inspection.propertyId;

// Helper function to normalize inspection data from Supabase format to app format
const normalizeInspection = (inspection) => {
  if (!inspection) return null;

  // If already in app format, return as-is
  if (inspection.rooms && !inspection.inspection_rooms) {
    logger.log('📝 normalizeInspection: already in app format', inspection);
    return inspection;
  }

  logger.log('📝 normalizeInspection: converting from Supabase format', inspection);

  // Convert from Supabase format
  const normalized = {
    ...inspection,
    propertyId: getPropertyId(inspection),
    tenant: inspection.leases?.tenant_name || '',
    overallNotes: inspection.overall_notes || '',
    rooms: (inspection.inspection_rooms || []).map(room => ({
      roomName: room.name,
      items: (room.inspection_items || []).map(item => {
        const photos = (item.inspection_photos || [])
          .map(photo => photo.url)
          .filter(url => url); // Filter out null/undefined URLs

        logger.log(`📷 Item "${item.name}": ${item.inspection_photos?.length || 0} photos in DB, ${photos.length} with valid URLs`, photos);

        return {
          name: item.name,
          condition: item.condition,
          notes: item.notes || '',
          photos: photos,
        };
      }),
    })),
  };

  logger.log('✅ normalizeInspection: result', normalized);
  return normalized;
};

// Room templates for different property types
const PROPERTY_TEMPLATES = {
  studio: ['Pokój główny', 'Łazienka', 'Aneks kuchenny'],
  '1-bedroom': ['Salon', 'Sypialnia', 'Kuchnia', 'Łazienka', 'Korytarz'],
  '2-bedroom': ['Salon', 'Sypialnia 1', 'Sypialnia 2', 'Kuchnia', 'Łazienka', 'Korytarz'],
  '3-bedroom': ['Salon', 'Sypialnia 1', 'Sypialnia 2', 'Sypialnia 3', 'Kuchnia', 'Łazienka 1', 'Łazienka 2', 'Korytarz'],
  house: ['Salon', 'Sypialnia 1', 'Sypialnia 2', 'Sypialnia 3', 'Kuchnia', 'Łazienka 1', 'Łazienka 2', 'Piwnica', 'Garaż', 'Ogród'],
};

// Standard items to check in each room
const ROOM_ITEMS = {
  common: [
    'Ściany (stan farby/tapet)',
    'Sufit',
    'Podłoga',
    'Drzwi',
    'Okna',
    'Parapety',
    'Grzejniki',
    'Kontakty elektryczne',
    'Oświetlenie',
  ],
  Kuchnia: [
    'Ściany (stan farby/tapet)',
    'Sufit',
    'Podłoga',
    'Drzwi',
    'Okna',
    'Szafki kuchenne (fronty)',
    'Blat kuchenny',
    'Zlew i bateria',
    'Lodówka',
    'Kuchenka/piekarnik',
    'Okap',
    'Płytki ścienne',
    'Kontakty elektryczne',
  ],
  Łazienka: [
    'Płytki ścienne',
    'Płytki podłogowe',
    'Wanna/prysznic',
    'Umywalka',
    'Toaleta',
    'Baterie',
    'Lustro',
    'Wentylacja',
    'Drzwi',
    'Sufit',
  ],
};

const CONDITIONS = {
  excellent: { label: 'Doskonały', color: '#10b981', icon: Sparkles },
  good: { label: 'Dobry', color: '#3b82f6', icon: CheckCircle },
  fair: { label: 'Średni', color: '#f59e0b', icon: AlertTriangle },
  poor: { label: 'Zły', color: '#ef4444', icon: XCircle },
};

const INSPECTION_TYPES = {
  'move-in': { label: 'Wprowadzenie', color: '#10b981' },
  'periodic': { label: 'Okresowa', color: '#3b82f6' },
  'move-out': { label: 'Wyprowadzka', color: '#ef4444' },
};

// Main App Component
export default function PropertyCheckPro() {
  const [view, setView] = useState('dashboard');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [wizardStep, setWizardStep] = useState(0);
  const [newInspection, setNewInspection] = useState(null);
  const [compareInspections, setCompareInspections] = useState([null, null]);
  const [showUpgradeModal, setShowUpgradeModal] = useState({
    isOpen: false,
    feature: '',
    message: '',
    upgradeContext: 'features', // 'features' or 'property_limit'
  });
  const [showAlert, setShowAlert] = useState({
    isOpen: false,
    title: '',
    message: '',
    variant: 'info',
  });
  const [isSavingInspection, setIsSavingInspection] = useState(false);
  const [invitedProperties, setInvitedProperties] = useState([]);

  // Use Supabase hooks
  const {
    properties,
    loading: propertiesLoading,
    error: propertiesError,
    createProperty,
    updateProperty,
    deleteProperty: deletePropertyService,
    refresh: refreshProperties
  } = useProperties();

  const {
    inspections,
    loading: inspectionsLoading,
    error: inspectionsError,
    createInspection: createInspectionService,
    refresh: refreshInspections
  } = useInspections(null); // null = fetch all inspections

  // Auth context for subscription checks
  const { user, profile, signOut, refreshProfile } = useAuth();

  // Navigation hook
  const navigate = useNavigate();

  // Microsoft Clarity Analytics - tracks user behavior (heatmaps, session recordings)
  useClarity();
  // Google Analytics 4
  useGoogleAnalytics();

  // Auto-logout on query timeout errors
  useEffect(() => {
    if (propertiesError?.includes?.('Query timeout') || inspectionsError?.includes?.('Query timeout')) {
      console.error('⚠️ Query timeout detected - forcing logout');
      signOut().then(() => {
        navigate('/login', { replace: true });
      });
    }
  }, [propertiesError, inspectionsError, signOut, navigate]);

  // Auto-logout if data doesn't load (RLS or connection issue)
  useEffect(() => {
    if (propertiesLoading || inspectionsLoading) {
      const timeoutId = setTimeout(async () => {
        console.warn('⚠️ Loading timeout (15s) - forcing logout');

        // Force logout to clear stuck queries
        await signOut();
        navigate('/login', { replace: true });
      }, 15000); // 15 seconds

      return () => clearTimeout(timeoutId);
    }
  }, [propertiesLoading, inspectionsLoading, navigate, signOut]);

  // Scroll to top whenever view changes (important for mobile)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [view]);

  // Load properties where user is tenant
  useEffect(() => {
    if (!user) return;

    async function loadInvitedProperties() {
      try {
        const tenantProps = await getTenantProperties();

        setInvitedProperties(tenantProps || []);
      } catch (error) {
        console.error('[App_old] Error loading tenant properties:', error);
      }
    }

    loadInvitedProperties();
  }, [user, profile]);

  // Debug: Log when invitedProperties changes
  useEffect(() => {
    logger.log('[App_old] invitedProperties state changed:', {
      count: invitedProperties?.length || 0,
      properties: invitedProperties
    });
  }, [invitedProperties]);

  // Handler to check if user can create new property
  const handleNewProperty = () => {
    if (!profile) return;

    // Check with current actual property count
    const permission = canCreateProperty(profile, properties.length);

    if (!permission.allowed) {
      setShowUpgradeModal({
        isOpen: true,
        feature: 'Dodatkowa nieruchomość',
        message: permission.reason || 'Plan darmowy pozwala na 1 nieruchomość. Przejdź na plan Pro, aby dodać więcej (do 5 nieruchomości).',
        upgradeContext: permission.upgradeContext || 'property_limit', // Use context from permission check
      });
      return;
    }

    setView('newProperty');
  };

  // Handler to check if user can create new inspection for property
  const handleNewInspection = (property) => {
    if (!profile) return;

    // Count current inspections for this property
    const propertyInspectionCount = inspections.filter(
      i => (i.property_id || i.propertyId) === property.id
    ).length;

    // Check per-property inspection limit
    const countPermission = canCreateInspectionForProperty(profile, propertyInspectionCount);

    if (!countPermission.allowed) {
      if (countPermission.requiresUpgrade) {
        setShowUpgradeModal({
          isOpen: true,
          feature: 'Więcej inspekcji',
          message: countPermission.reason
        });
      } else {
        setShowAlert({
          isOpen: true,
          title: 'Osiągnięto limit',
          message: countPermission.reason,
          variant: 'info'
        });
      }
      return;
    }

    // Proceed to type selection
    setSelectedProperty(property);
    setView('selectType');
  };

  // Helper: Find property in both owned and invited properties
  const findPropertyById = (propertyId) => {
    return properties.find(p => p.id === propertyId) ||
           invitedProperties.find(p => p.id === propertyId);
  };

  // Handler to start Pro trial
  const handleStartTrial = async () => {
    const result = await startProTrial();

    if (result.success) {
      await refreshProfile();
      setShowUpgradeModal({ isOpen: false, feature: '', message: '', upgradeContext: 'features' });
      showAlertDialog('Gratulacje! Rozpocząłeś 14-dniowy okres próbny planu Pro.', 'Sukces', 'success');
    } else {
      showAlertDialog(result.error, 'Błąd', 'error');
    }
  };

  // Handler to upgrade to paid Pro plan
  const handleUpgradePaid = async (billingPeriod = 'monthly') => {
    logger.log('[App] Starting Stripe checkout:', billingPeriod);

    // DON'T close modal - it will show loading spinner until redirect
    // Modal will disappear automatically when page redirects to Stripe

    // Create Stripe checkout session
    const result = await createCheckoutSession(billingPeriod);

    if (!result.success) {
      console.error('[App] Checkout error:', result.error);
      // Close modal only on error
      setShowUpgradeModal({ isOpen: false, feature: '', message: '', upgradeContext: 'features' });
      showAlertDialog(
        result.error || 'Wystąpił błąd podczas przekierowania do płatności. Spróbuj ponownie.',
        'Błąd płatności',
        'error'
      );
    }
    // If successful, user will be redirected to Stripe Checkout (modal disappears on page redirect)
  };

  // Handler to close upgrade modal
  const handleCloseUpgradeModal = () => {
    setShowUpgradeModal({ isOpen: false, feature: '', message: '' });
  };

  // Handler to show alert (replacement for alert())
  const showAlertDialog = (message, title = '', variant = 'info') => {
    setShowAlert({
      isOpen: true,
      title,
      message,
      variant,
    });
  };

  // Handler to close alert
  const handleCloseAlert = () => {
    setShowAlert({ isOpen: false, title: '', message: '', variant: 'info' });
  };

  const saveProperty = async (property) => {
    const result = await createProperty({
      // Basic
      type: property.type,

      // Address
      street: property.street,
      buildingNumber: property.buildingNumber,
      apartmentNumber: property.apartmentNumber || null,
      city: property.city,
      postalCode: property.postalCode,
      countryCode: property.countryCode || 'PL',

      // Property attributes
      hasGarage: property.hasGarage || false,
      hasStorageRoom: property.hasStorageRoom || false,
      totalArea: property.totalArea || null,
      floorNumber: property.floorNumber || null,

      // Rental status (general availability)
      rentalStatus: property.rentalStatus || 'available',

      // File paths
      primaryPhotoPath: property.primaryPhotoPath || null,
      contractAttachmentPath: property.contractAttachmentPath || null,
    });

    if (result.success) {
      await refreshProperties();
      await refreshProfile(); // Refresh profile to update properties_count
      trackPixelCustomEvent('AddProperty');
      return result.data; // Return created property with ID
    } else {
      showAlertDialog(result.error, 'Błąd podczas zapisywania nieruchomości', 'error');
      throw new Error(result.error);
    }
  };

  const updatePropertyData = async (id, propertyData) => {
    try {
      const result = await updateProperty(id, {
        // Basic
        type: propertyData.type,

        // Address
        street: propertyData.street,
        buildingNumber: propertyData.buildingNumber,
        apartmentNumber: propertyData.apartmentNumber || null,
        city: propertyData.city,
        postalCode: propertyData.postalCode,
        countryCode: propertyData.countryCode || 'PL',

        // Property attributes
        hasGarage: propertyData.hasGarage || false,
        hasStorageRoom: propertyData.hasStorageRoom || false,
        totalArea: propertyData.totalArea || null,
        floorNumber: propertyData.floorNumber || null,

        // Rental status (general availability)
        rentalStatus: propertyData.rentalStatus || 'available',

        // File paths
        primaryPhotoPath: propertyData.primaryPhotoPath || null,
        contractAttachmentPath: propertyData.contractAttachmentPath || null,
      });

      await refreshProperties();
      showAlertDialog('Nieruchomość została zaktualizowana.', 'Sukces', 'success');
      return result;
    } catch (error) {
      showAlertDialog(error.message || 'Nie udało się zaktualizować nieruchomości', 'Błąd', 'error');
      throw error;
    }
  };

  const deleteProperty = async (id) => {
    const result = await deletePropertyService(id);

    if (result.success) {
      await refreshProperties();
      await refreshInspections();
      await refreshProfile(); // Update properties_count after deletion
    } else {
      showAlertDialog(result.error, 'Błąd podczas usuwania nieruchomości', 'error');
    }
  };

  const saveInspection = async (inspection) => {
    // Transform the inspection data to match Supabase schema
    const inspectionData = {
      propertyId: inspection.propertyId,
      type: inspection.type,
      date: inspection.date,
      inspector: inspection.inspector,
      overallNotes: inspection.overallNotes,
      leaseId: inspection.leaseId || null,
      rooms: inspection.rooms.map((room, roomIndex) => ({
        name: room.roomName,
        displayOrder: roomIndex,
        items: room.items.map((item, itemIndex) => ({
          name: item.name,
          condition: item.condition,
          notes: item.notes,
          displayOrder: itemIndex,
          photos: item.photos || [], // Include photos for upload
        })),
      })),
    };

    // Find property to get owner ID
    const property = findPropertyById(inspection.propertyId);

    // Check if user is tenant (not owner) for this property
    const isUserOwner = property?.user_id === profile?.id;
    const isUserTenant = invitedProperties.some(p => p.id === inspection.propertyId);

    let result;

    try {
      if (isUserTenant && !isUserOwner) {
        // User is tenant - use tenant inspection creation
        result = await createTenantInspection(inspectionData, property.user_id);
      } else {
        // User is owner - use normal inspection creation
        result = await createInspectionService(inspectionData);
      }

      if (result) {
        await refreshInspections();
        trackPixelCustomEvent('CompleteInspection');
        return result;
      } else {
        showAlertDialog('Nie udało się zapisać inspekcji', 'Błąd', 'error');
        return null;
      }
    } catch (error) {
      console.error('Error saving inspection:', error);
      showAlertDialog(error.message || 'Błąd podczas zapisywania inspekcji', 'Błąd', 'error');
      return null;
    }
  };

  const startNewInspection = (property, type) => {
    if (!profile) {
      showAlertDialog('Trwa ładowanie profilu użytkownika. Spróbuj ponownie za chwilę.', 'Proszę czekać', 'info');
      return;
    }

    // Check if inspection type is allowed
    const typePermission = canCreateInspection(profile, type);

    if (!typePermission.allowed) {
      if (typePermission.requiresUpgrade) {
        setShowUpgradeModal({
          isOpen: true,
          feature: 'Inspekcja okresowa',
          message: typePermission.reason,
        });
      } else {
        showAlertDialog(typePermission.reason, 'Limit inspekcji', 'warning');
      }
      return;
    }

    // Check per-property inspection limit
    const propertyInspectionCount = inspections.filter(
      i => (i.property_id || i.propertyId) === property.id
    ).length;

    const countPermission = canCreateInspectionForProperty(profile, propertyInspectionCount);

    if (!countPermission.allowed) {
      if (countPermission.requiresUpgrade) {
        setShowUpgradeModal({
          isOpen: true,
          feature: 'Więcej inspekcji',
          message: countPermission.reason,
        });
      } else {
        showAlertDialog(countPermission.reason, 'Limit inspekcji', 'warning');
      }
      return;
    }

    const rooms = PROPERTY_TEMPLATES[property.type].map(roomName => ({
      roomName,
      items: (ROOM_ITEMS[roomName] || ROOM_ITEMS.common).map(name => ({
        name,
        condition: 'good',
        notes: '',
        photos: [],
      })),
    }));

    setNewInspection({
      propertyId: property.id,
      property: property,
      type,
      date: new Date().toISOString().split('T')[0],
      inspector: user?.user_metadata?.full_name || '',
      tenant: property.tenant || '',
      rooms,
      overallNotes: '',
    });
    setWizardStep(0);
    setView('wizard');
    trackPixelCustomEvent('StartInspection');
  };

  const generatePDF = async (inspection) => {
    // PDF is now available for all users (Free + Pro)
    try {
      const property = findPropertyById(inspection.propertyId);
      if (!property) {
        showAlertDialog('Nie znaleziono danych nieruchomości.', 'Błąd', 'error');
        return;
      }

      await generateInspectionPDF(inspection, property);
    } catch (error) {
      console.error('Error generating PDF:', error);
      showAlertDialog('Błąd podczas generowania PDF. Spróbuj ponownie.', 'Błąd', 'error');
    }
  };

  const handleDeleteInspection = async (inspectionId) => {
    try {
      await deleteInspection(inspectionId);
      await refreshInspections();
      setView('dashboard');
      showAlertDialog('Inspekcja została usunięta.', 'Sukces', 'success');
    } catch (error) {
      console.error('Error deleting inspection:', error);
      showAlertDialog('Nie udało się usunąć inspekcji. Spróbuj ponownie.', 'Błąd', 'error');
    }
  };

  const handleGenerateComparisonPDF = async () => {
    // PDF comparison available for all users (Free + Pro)
    try {
      if (!compareInspections[0] || !compareInspections[1]) {
        showAlertDialog('Brak wybranych inspekcji do porównania.', 'Błąd', 'error');
        return;
      }

      if (!selectedProperty) {
        showAlertDialog('Nie znaleziono danych nieruchomości.', 'Błąd', 'error');
        return;
      }

      // compareInspections contains normalized data, we need the raw Supabase format
      // Find the original inspections from the inspections array (which has both formats mixed)
      const originalInspections = inspections.filter(i =>
        i.id === compareInspections[0].id || i.id === compareInspections[1].id
      );

      if (originalInspections.length !== 2) {
        showAlertDialog('Nie można załadować danych inspekcji.', 'Błąd', 'error');
        return;
      }

      // Sort to match compareInspections order (first inspection is "before", second is "after")
      const inspection1 = originalInspections.find(i => i.id === compareInspections[0].id);
      const inspection2 = originalInspections.find(i => i.id === compareInspections[1].id);

      await generateComparisonPDF(inspection1, inspection2, selectedProperty);
    } catch (error) {
      console.error('Error generating comparison PDF:', error);
      showAlertDialog('Błąd podczas generowania PDF porównania. Spróbuj ponownie.', 'Błąd', 'error');
    }
  };

  // Show loading state
  if (propertiesLoading || inspectionsLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Ładowanie danych...</p>
        </div>
      </div>
    );
  }

  // Handler for upgrade button in trial banner
  const handleTrialUpgradeClick = () => {
    setShowUpgradeModal({
      isOpen: true,
      feature: 'Plan Pro',
      message: 'Przejdź na płatną subskrypcję i zachowaj pełny dostęp do funkcji Pro po zakończeniu okresu próbnego.'
    });
  };

  // Render different views
  if (view === 'dashboard') {
    return (
      <Layout onUpgrade={handleTrialUpgradeClick}>
        <Dashboard
          user={user}
          profile={profile}
          properties={properties}
          inspections={inspections}
          invitedProperties={invitedProperties}
          onNewProperty={handleNewProperty}
          onNewInspection={handleNewInspection}
          onShowUpgradeModal={(feature, message) => {
            setShowUpgradeModal({
              isOpen: true,
              feature: feature,
              message: message
            });
          }}
          onViewInspections={(property) => {
            setSelectedProperty(property);
            setView('propertyDetail');
          }}
          onViewInspection={(inspection) => {
            setSelectedInspection(inspection);
            setView('inspectionView');
          }}
          onCompareInspections={(property) => {
            setSelectedProperty(property);
            setView('compareSelect');
          }}
          onDeleteProperty={deleteProperty}
          findPropertyById={findPropertyById}
        />

        {/* Upgrade Modal */}
        <UpgradeModal
          isOpen={showUpgradeModal.isOpen}
          onClose={handleCloseUpgradeModal}
          feature={showUpgradeModal.feature}
          message={showUpgradeModal.message}
          upgradeContext={showUpgradeModal.upgradeContext}
          profile={profile}
          user={user}
          refreshProfile={refreshProfile}
          onStartTrial={handleStartTrial}
          onUpgradePaid={handleUpgradePaid}
        />

        {/* Alert Dialog */}
        <AlertDialog
          isOpen={showAlert.isOpen}
          title={showAlert.title}
          message={showAlert.message}
          variant={showAlert.variant}
          onClose={handleCloseAlert}
        />
      </Layout>
    );
  }

  if (view === 'newProperty') {
    return <NewPropertyForm
      onSave={async (property) => {
        const createdProperty = await saveProperty(property);
        setView('dashboard');
        return createdProperty;
      }}
      onUpdate={async (id, property) => {
        const updatedProperty = await updatePropertyData(id, property);
        return updatedProperty;
      }}
      onCancel={() => setView('dashboard')}
      showAlert={showAlertDialog}
      onUpgrade={handleTrialUpgradeClick}
    />;
  }

  if (view === 'editProperty') {
    return <NewPropertyForm
      property={selectedProperty}
      onSave={async (property) => {
        const updatedProperty = await updatePropertyData(selectedProperty.id, property);
        // Refresh all data after update
        await refreshInspections();
        // Fetch fresh property data from database
        try {
          const freshProperty = await getProperty(selectedProperty.id);
          if (freshProperty) {
            setSelectedProperty(freshProperty);
          } else {
            setSelectedProperty(updatedProperty);
          }
        } catch (error) {
          console.error('Error fetching updated property:', error);
          setSelectedProperty(updatedProperty);
        }
        setView('propertyDetail');
        return updatedProperty;
      }}
      onUpdate={async (id, property) => {
        const updatedProperty = await updatePropertyData(id, property);
        // Refresh all data after update
        await refreshInspections();
        // Fetch fresh property data from database
        try {
          const freshProperty = await getProperty(id);
          if (freshProperty) {
            setSelectedProperty(freshProperty);
          } else {
            setSelectedProperty(updatedProperty);
          }
        } catch (error) {
          console.error('Error fetching updated property:', error);
          setSelectedProperty(updatedProperty);
        }
        return updatedProperty;
      }}
      onCancel={() => setView('propertyDetail')}
      showAlert={showAlertDialog}
      onUpgrade={handleTrialUpgradeClick}
    />;
  }

  if (view === 'selectType') {
    return (
      <Layout onUpgrade={handleTrialUpgradeClick}>
        <SelectInspectionType
          property={selectedProperty}
          profile={profile}
          inspections={inspections}
          onSelect={(type) => startNewInspection(selectedProperty, type)}
          onCancel={() => setView('dashboard')}
          onShowUpgradeModal={(feature, message) => {
            setShowUpgradeModal({
              isOpen: true,
              feature,
              message,
            });
          }}
        />

        {/* Upgrade Modal */}
        <UpgradeModal
          isOpen={showUpgradeModal.isOpen}
          onClose={handleCloseUpgradeModal}
          feature={showUpgradeModal.feature}
          message={showUpgradeModal.message}
          upgradeContext={showUpgradeModal.upgradeContext}
          profile={profile}
          user={user}
          refreshProfile={refreshProfile}
          onStartTrial={handleStartTrial}
          onUpgradePaid={handleUpgradePaid}
        />
      </Layout>
    );
  }

  if (view === 'wizard') {
    return (
      <InspectionWizard
        inspection={newInspection}
        step={wizardStep}
        isSaving={isSavingInspection}
        profile={profile}
        user={user}
        refreshProfile={refreshProfile}
        onNext={() => setWizardStep(s => s + 1)}
        onPrev={() => setWizardStep(s => s - 1)}
        onChange={setNewInspection}
        onSave={async (inspection) => {
          setIsSavingInspection(true);
          try {
            const result = await saveInspection(inspection);
            if (result) {
              setView('dashboard');
              setNewInspection(null);
              setWizardStep(0);
            }
          } finally {
            setIsSavingInspection(false);
          }
        }}
        onCancel={() => {
          setView('dashboard');
          setNewInspection(null);
          setWizardStep(0);
        }}
        onUpgrade={handleTrialUpgradeClick}
      />
    );
  }

  if (view === 'propertyDetail') {
    const propertyInspections = inspections.filter(i => getPropertyId(i) === selectedProperty.id);
    return (
      <Layout onUpgrade={handleTrialUpgradeClick}>
        <PropertyDetail
          property={selectedProperty}
          inspections={propertyInspections}
          onBack={() => setView('dashboard')}
          onViewInspection={(inspection) => {
            setSelectedInspection(inspection);
            setView('inspectionView');
          }}
          onNewInspection={() => {
            setView('selectType');
          }}
          onEdit={() => {
            setView('editProperty');
          }}
          onRefreshProperty={async () => {
            await refreshProperties();
            // Fetch the updated property directly from database to get fresh rental_status
            try {
              const updatedProperty = await getProperty(selectedProperty.id);
              if (updatedProperty) {
                setSelectedProperty(updatedProperty);
              }
            } catch (error) {
              console.error('Error refreshing property:', error);
            }
          }}
          showAlert={showAlertDialog}
        />

        {/* Alert Dialog */}
        <AlertDialog
          isOpen={showAlert.isOpen}
          title={showAlert.title}
          message={showAlert.message}
          variant={showAlert.variant}
          onClose={handleCloseAlert}
        />
      </Layout>
    );
  }

  if (view === 'inspectionView') {
    return (
      <Layout onUpgrade={handleTrialUpgradeClick}>
        <InspectionView
          inspection={normalizeInspection(selectedInspection)}
          property={findPropertyById(getPropertyId(selectedInspection))}
          onBack={() => setView('dashboard')}
          onGeneratePDF={generatePDF}
          onDelete={handleDeleteInspection}
        />
      </Layout>
    );
  }

  if (view === 'compareSelect') {
    const propertyInspections = inspections.filter(i => getPropertyId(i) === selectedProperty.id);
    return (
      <Layout onUpgrade={handleTrialUpgradeClick}>
        <CompareSelect
          property={selectedProperty}
          inspections={propertyInspections}
          onCompare={(insp1, insp2) => {
            setCompareInspections([
              normalizeInspection(insp1),
              normalizeInspection(insp2)
            ]);
            setView('compareView');
          }}
          onBack={() => setView('dashboard')}
        />
      </Layout>
    );
  }

  if (view === 'compareView') {
    return (
      <Layout onUpgrade={handleTrialUpgradeClick}>
        <CompareView
          inspections={compareInspections}
          property={selectedProperty}
          onBack={() => setView('dashboard')}
          onGeneratePDF={handleGenerateComparisonPDF}
        />
      </Layout>
    );
  }

  return null;
}

// Property Image Component with proper URL handling
function PropertyImage({ property }) {
  const { url: photoUrl, loading } = usePropertyPhotoUrl(property.primary_photo_path);

  if (loading) {
    return (
      <div className="w-16 h-16 bg-slate-100 rounded-lg flex items-center justify-center">
        <div className="w-4 h-4 border-2 border-violet-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={property.address}
        className="w-16 h-16 object-cover rounded-lg"
      />
    );
  }

  return (
    <div className="w-16 h-16 bg-gradient-to-br from-slate-100 to-slate-200 rounded-lg flex items-center justify-center">
      <Home className="w-8 h-8 text-slate-400" />
    </div>
  );
}

// Attention Needed Component
function AttentionNeeded({ properties, inspections, leases, profile, onViewProperty, onViewInspection, onUpgrade }) {
  // Collapse state with localStorage persistence
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('attention-section-collapsed');
    return saved === 'true';
  });

  const toggleCollapse = () => {
    setIsCollapsed(prev => {
      const newValue = !prev;
      localStorage.setItem('attention-section-collapsed', String(newValue));
      return newValue;
    });
  };

  // Calculate attention items
  const attentionItems = useMemo(() => {
    const items = [];
    const now = new Date();
    const ninetyDaysAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

    // Helper for Polish day pluralization
    const pluralizeDays = (count) => {
      if (count === 1) return 'dzień';
      if (count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 10 || count % 100 >= 20)) return 'dni';
      return 'dni';
    };

    // 1. Trial info (if applicable)
    if (profile && (profile.subscription_plan === 'pro' || profile.subscription_plan === 'starter') && profile.trial_ends_at && !profile.stripe_subscription_id) {
      const trialEnd = new Date(profile.trial_ends_at);
      if (trialEnd > now) {
        const daysRemaining = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
        const planName = profile.subscription_plan === 'starter' ? 'Starter' : 'Pro';
        items.push({
          id: 'trial-expiring',
          type: 'trial',
          icon: Calendar,
          title: `Okres próbny kończy się za ${daysRemaining} ${pluralizeDays(daysRemaining)}`,
          description: `Przejdź na plan ${planName}, aby kontynuować.`,
          actionLabel: `Kup plan ${planName}`,
          onClick: onUpgrade
        });
      }
    }

    // 2. Available properties
    const availableProperties = properties.filter(p => p.rental_status === 'available');
    availableProperties.forEach(property => {
      items.push({
        id: `available-${property.id}`,
        type: 'available',
        icon: Home,
        title: 'Nieruchomość bez aktywnej umowy',
        description: `Uzupełnij dane najmu, aby śledzić historię umów: ${formatPropertyAddress(property)}`,
        actionLabel: 'Przejdź do nieruchomości',
        property,
        onClick: () => onViewProperty(property)
      });
    });

    // 3. Properties without inspections in 90+ days
    properties.forEach(property => {
      const propertyInspections = inspections.filter(i =>
        (i.property_id || i.propertyId) === property.id
      );

      if (propertyInspections.length === 0) {
        items.push({
          id: `no-inspection-${property.id}`,
          type: 'no-inspection',
          icon: Calendar,
          title: `Brak inspekcji dla nieruchomości ${formatPropertyAddress(property)}`,
          description: 'Dodaj pierwszą inspekcję.',
          actionLabel: 'Dodaj inspekcję',
          property,
          onClick: () => onViewProperty(property)
        });
      } else {
        const latestInspection = propertyInspections.sort((a, b) =>
          new Date(b.date) - new Date(a.date)
        )[0];
        const inspectionDate = new Date(latestInspection.date);

        if (inspectionDate < ninetyDaysAgo) {
          const daysSince = Math.floor((now - inspectionDate) / (1000 * 60 * 60 * 24));
          items.push({
            id: `old-inspection-${property.id}`,
            type: 'old-inspection',
            icon: Calendar,
            title: `Brak inspekcji dla nieruchomości ${formatPropertyAddress(property)}`,
            description: `Ostatnia inspekcja była ${daysSince} ${pluralizeDays(daysSince)} temu.`,
            actionLabel: 'Dodaj inspekcję',
            property,
            onClick: () => onViewProperty(property)
          });
        }
      }
    });

    // 4. Active leases ending within 30 days
    const activeLeases = leases.filter(l => l.status === 'active' && l.end_date);
    activeLeases.forEach(lease => {
      const endDate = new Date(lease.end_date);
      if (endDate <= thirtyDaysFromNow && endDate >= now) {
        const daysUntil = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
        const property = properties.find(p => p.id === lease.property_id);
        if (property) {
          items.push({
            id: `lease-ending-${lease.id}`,
            type: 'lease-ending',
            icon: Calendar,
            title: `Koniec najmu za ${daysUntil} ${pluralizeDays(daysUntil)}`,
            description: `${lease.tenant_name} - ${formatPropertyAddress(property)}`,
            actionLabel: 'Przejdź do nieruchomości',
            property,
            onClick: () => onViewProperty(property)
          });
        }
      }
    });

    // 5. Recent inspections with important notes
    const recentInspectionsWithNotes = inspections
      .filter(i => i.overall_notes && i.overall_notes.trim().length > 0)
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 3); // Show max 3

    recentInspectionsWithNotes.forEach(inspection => {
      const property = properties.find(p => p.id === (inspection.property_id || inspection.propertyId));
      if (property) {
        items.push({
          id: `notes-${inspection.id}`,
          type: 'inspection-notes',
          icon: AlertTriangle,
          iconColor: 'text-amber-500',
          title: `Uwagi do inspekcji: ${formatPropertyAddress(property)}`,
          description: inspection.overall_notes,
          actionLabel: 'Otwórz inspekcję',
          inspection,
          onClick: () => onViewInspection(inspection)
        });
      }
    });

    return items;
  }, [properties, inspections, leases, profile, onViewProperty, onViewInspection, onUpgrade]);

  if (attentionItems.length === 0) {
    return null;
  }

  return (
    <div className="mb-8">
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-slate-800">
            Wymaga uwagi ({attentionItems.length})
          </h2>
          <button
            onClick={toggleCollapse}
            className="p-1 rounded-lg hover:bg-slate-100 transition-colors text-slate-600"
            aria-label={isCollapsed ? 'Rozwiń sekcję' : 'Zwiń sekcję'}
          >
            <ChevronDown className={`w-5 h-5 transition-transform ${isCollapsed ? 'rotate-180' : ''}`} />
          </button>
        </div>
        {!isCollapsed && (
          <div className="space-y-3">
            {attentionItems.map(item => {
              const IconComponent = item.icon;
              return (
                <div
                  key={item.id}
                  className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 p-4 rounded-lg hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-200"
                >
                  {/* Ikona + tekst */}
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={`flex-shrink-0 ${item.iconColor || 'text-slate-600'}`}>
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-slate-900 text-sm mb-1">
                        {item.title}
                      </h3>
                      <p className="text-sm text-slate-600 line-clamp-2">
                        {item.description}
                      </p>
                    </div>
                  </div>
                  {/* Przycisk - full width na mobile */}
                  <button
                    onClick={item.onClick}
                    className="w-full sm:w-auto sm:flex-shrink-0 text-violet-600 hover:text-violet-700 font-medium text-sm px-4 py-2 rounded-lg border border-violet-200 hover:bg-violet-50 transition-colors text-center"
                  >
                    {item.actionLabel}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// Dashboard Component
function Dashboard({ user, profile, properties, inspections, invitedProperties, onNewProperty, onNewInspection, onShowUpgradeModal, onViewInspections, onViewInspection, onCompareInspections, onDeleteProperty, findPropertyById }) {
  const [showPropertySelectModal, setShowPropertySelectModal] = useState(false);
  const [allLeases, setAllLeases] = useState([]);
  const [leasesLoading, setLeasesLoading] = useState(true);

  // Fetch all user leases
  useEffect(() => {
    async function fetchAllLeases() {
      try {
        setLeasesLoading(true);
        const { getAllUserLeases } = await import('./services/supabase/leases');
        const leases = await getAllUserLeases();
        setAllLeases(leases);
      } catch (error) {
        console.error('Error fetching leases:', error);
        setAllLeases([]);
      } finally {
        setLeasesLoading(false);
      }
    }
    fetchAllLeases();
  }, []);

  // Split inspections into owned and tenant
  const ownedPropertyIds = properties.map(p => p.id);
  const invitedPropertyIds = invitedProperties.map(p => p.id);

  const ownedInspections = inspections.filter(i =>
    ownedPropertyIds.includes(i.property_id || i.propertyId)
  );
  const tenantInspections = inspections.filter(i =>
    invitedPropertyIds.includes(i.property_id || i.propertyId)
  );

  // Recent inspections (combine both, mark tenant ones)
  const recentInspections = [...inspections]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 5)
    .map(inspection => ({
      ...inspection,
      isTenantInspection: invitedPropertyIds.includes(inspection.property_id || inspection.propertyId)
    }));

  // Get user's first name for greeting
  const firstName = user?.user_metadata?.full_name?.split(' ')[0] || 'Użytkowniku';

  const handleNewInspectionClick = () => {
    const totalProperties = properties.length + invitedProperties.length;

    if (totalProperties === 0) {
      return; // Button is disabled anyway
    }

    if (totalProperties === 1) {
      // Only one property (either owned or invited), go directly
      const singleProperty = properties.length > 0 ? properties[0] : invitedProperties[0];
      onNewInspection(singleProperty);
    } else {
      // Multiple properties, show selection modal
      setShowPropertySelectModal(true);
    }
  };

  const handlePropertySelect = (property, isLocked) => {
    if (isLocked) {
      // Show upgrade modal for locked properties
      setShowPropertySelectModal(false);
      onShowUpgradeModal(
        'Dostęp do nieruchomości',
        'Ta nieruchomość jest dostępna tylko w planie Pro. Przejdź na plan Pro, aby uzyskać dostęp do 5 nieruchomości.'
      );
      return;
    }

    setShowPropertySelectModal(false);
    onNewInspection(property);
  };

  // Scroll to section handlers
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const yOffset = -100; // Offset for fixed header
      const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="mb-8 bg-gradient-to-br from-violet-500 via-purple-600 to-indigo-700 rounded-2xl p-6 md:p-8 text-white shadow-xl relative overflow-hidden">
          {/* Decorative check marks in background */}
          <div className="absolute top-0 right-0 opacity-5 blur-sm">
            <Check className="w-96 h-96 -rotate-12 translate-x-1/3 -translate-y-1/3" strokeWidth={1.5} />
          </div>
          <div className="absolute bottom-0 left-0 opacity-10 blur-[2px]">
            <Check className="w-64 h-64 rotate-45 -translate-x-1/4 translate-y-1/4" strokeWidth={2} />
          </div>
          <div className="absolute top-1/2 right-1/4 opacity-5 blur-sm">
            <Check className="w-48 h-48 -rotate-6" strokeWidth={1} />
          </div>

          {/* Content with relative positioning */}
          <div className="relative z-10">
            <h1 className="text-2xl md:text-3xl font-bold mb-2 drop-shadow-lg">
              Cześć {firstName}, od czego chcesz zacząć?
            </h1>
            <p className="text-violet-100 mb-6 text-sm md:text-base drop-shadow-md">
              Zarządzaj swoimi nieruchomościami i inspekcjami w jednym miejscu
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={onNewProperty}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-white text-violet-600 rounded-xl hover:bg-violet-50 transition-all duration-300 font-medium shadow-md hover:shadow-lg"
              >
                <Plus className="w-5 h-5" />
                Dodaj nieruchomość
              </button>
              <button
                onClick={handleNewInspectionClick}
                disabled={properties.length === 0 && invitedProperties.length === 0}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-sm text-white rounded-xl hover:bg-white/20 transition-all duration-300 font-medium border-2 border-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                title={properties.length === 0 && invitedProperties.length === 0 ? 'Brak dostępnych nieruchomości' : 'Dodaj nową inspekcję'}
              >
                <Plus className="w-5 h-5" />
                Dodaj inspekcję
              </button>
            </div>
          </div>
        </div>

        {/* Property Selection Modal */}
        {showPropertySelectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-slate-800">Wybierz nieruchomość</h3>
                  <button
                    onClick={() => setShowPropertySelectModal(false)}
                    className="text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <p className="text-sm text-slate-500 mt-1">Dla której nieruchomości chcesz utworzyć inspekcję?</p>
              </div>
              <div className="p-6 overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Owned properties */}
                  {properties.map((property, index) => {
                    // Check if property is locked (beyond subscription limit)
                    const limits = profile ? getSubscriptionLimits(profile.subscription_plan, profile) : null;
                    const maxProperties = limits?.maxProperties || 1;
                    const isLocked = index >= maxProperties;

                    return (
                      <button
                        key={property.id}
                        onClick={() => handlePropertySelect(property, isLocked)}
                        disabled={isLocked}
                        className={`text-left p-4 border border-slate-200 rounded-xl transition-all group relative ${
                          isLocked
                            ? 'opacity-60 cursor-not-allowed bg-slate-50'
                            : 'hover:shadow-lg hover:scale-105'
                        }`}
                      >
                        {isLocked && (
                          <div className="absolute top-2 right-2">
                            <span className="px-2 py-1 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-full text-xs font-bold">
                              🔒 PRO
                            </span>
                          </div>
                        )}
                        <div className="flex items-start gap-3">
                          <PropertyImage property={property} className={isLocked ? 'grayscale' : ''} />
                          <div className="flex-1 min-w-0">
                            <h4 className={`font-semibold transition-colors mb-1 text-base leading-tight ${
                              isLocked ? 'text-slate-500' : 'text-slate-800 group-hover:text-slate-800'
                            }`}>
                              {property.street} {property.building_number}
                              {property.apartment_number && `/${property.apartment_number}`}
                            </h4>
                            <p className={`text-sm mb-2 ${isLocked ? 'text-slate-400' : 'text-slate-600'}`}>
                              {property.postal_code} {property.city}
                            </p>
                            <div className={`flex items-center gap-2 text-xs ${isLocked ? 'text-slate-300' : 'text-slate-400'}`}>
                              <FileText className="w-3 h-3" />
                              <span>{inspections.filter(i => getPropertyId(i) === property.id).length} inspekcji</span>
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}

                  {/* Invited properties (tenant role) */}
                  {invitedProperties.map((property) => (
                    <button
                      key={property.id}
                      onClick={() => {
                        setShowPropertySelectModal(false);
                        onNewInspection(property);
                      }}
                      className="text-left p-4 border border-slate-200 rounded-xl transition-all group hover:shadow-lg hover:scale-105"
                    >
                      <div className="flex items-start gap-3">
                        <PropertyImage property={property} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="pc-badge-role inline-flex items-center gap-1">
                              <UserCheck className="w-3 h-3" />
                              Najemca
                            </span>
                          </div>
                          <h4 className="font-semibold transition-colors mb-1 text-base leading-tight text-slate-800 group-hover:text-slate-800">
                            {property.street} {property.building_number}
                            {property.apartment_number && `/${property.apartment_number}`}
                          </h4>
                          <p className="text-sm mb-2 text-slate-600">
                            {property.postal_code} {property.city}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-slate-400">
                            <FileText className="w-3 h-3" />
                            <span>{inspections.filter(i => getPropertyId(i) === property.id).length} inspekcji</span>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}


        {/* Attention Needed Section */}
        {!leasesLoading && (
          <AttentionNeeded
            properties={properties}
            inspections={inspections}
            leases={allLeases}
            profile={profile}
            onViewProperty={onViewInspections}
            onViewInspection={onViewInspection}
            onUpgrade={() => onShowUpgradeModal('Plan Pro', 'Przejdź na plan Pro, aby kontynuować po okresie próbnym.')}
          />
        )}

        {/* Properties List - unified (owned + invited) */}
        <div id="properties-section" className="mb-8">
          <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Home className="w-5 h-5 pc-icon" />
            Nieruchomości
          </h2>

          {properties.length === 0 && invitedProperties.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center border border-slate-200/50 shadow-sm">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Home className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Brak nieruchomości</h3>
              <p className="text-slate-500 mb-6">Dodaj swoją pierwszą nieruchomość, aby rozpocząć inspekcje</p>
              <button
                onClick={onNewProperty}
                className="px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl hover:shadow-lg hover:shadow-violet-500/30 transition-all duration-300 hover:scale-105 font-medium inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Dodaj pierwszą nieruchomość
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Owned properties */}
              {properties.map((property, index) => {
                // Check if property is locked (beyond subscription limit)
                const limits = profile ? getSubscriptionLimits(profile.subscription_plan, profile) : null;
                const maxProperties = limits?.maxProperties || 1;
                const isLocked = index >= maxProperties;

                return (
                  <PropertyCard
                    key={property.id}
                    property={property}
                    inspections={inspections.filter(i => getPropertyId(i) === property.id)}
                    isLocked={isLocked}
                    role="owner"
                    onNewInspection={onNewInspection}
                    onView={onViewInspections}
                    onCompare={onCompareInspections}
                    onDelete={onDeleteProperty}
                    onShowUpgradeModal={() => {
                      onShowUpgradeModal('Dostęp do nieruchomości', 'Ta nieruchomość jest dostępna tylko w planie Pro. Przejdź na plan Pro, aby uzyskać dostęp do 5 nieruchomości.');
                    }}
                  />
                );
              })}

              {/* Invited properties (tenant role) */}
              {invitedProperties.map((property) => (
                <PropertyCard
                  key={property.id}
                  property={property}
                  inspections={inspections.filter(i => getPropertyId(i) === property.id)}
                  role="tenant"
                  invitation={property.invitation}
                  onNewInspection={onNewInspection}
                  onView={onViewInspections}
                />
              ))}
            </div>
          )}
        </div>

        {/* Recent Inspections */}
        {recentInspections.length > 0 && (
          <div id="inspections-section">
            <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 pc-icon" />
              Ostatnie inspekcje
            </h2>
            <div className="space-y-3">
              {recentInspections.map(inspection => {
                const property = findPropertyById(getPropertyId(inspection));
                const tenantName = inspection.leases?.tenant_name || 'Brak najemcy';

                // Format property address
                const formatPropertyAddress = () => {
                  if (!property) return 'Nieznana lokalizacja';
                  if (property.street && property.city) {
                    const parts = [];
                    let addressLine = property.street;
                    if (property.building_number) addressLine += ` ${property.building_number}`;
                    if (property.apartment_number) addressLine += `/${property.apartment_number}`;
                    parts.push(addressLine);
                    if (property.city) parts.push(property.city);
                    return parts.join(', ');
                  }
                  return property.address || 'Brak adresu';
                };

                return (
                  <div
                    key={inspection.id}
                    className="bg-white rounded-2xl p-5 border border-slate-200/50 shadow-sm hover:shadow-md hover:scale-105 transition-all duration-300 cursor-pointer group"
                    onClick={() => onViewInspection(inspection)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <FileText className="w-6 h-6 text-slate-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-slate-800">
                              {INSPECTION_TYPES[inspection.type].label}
                            </h3>
                            {inspection.isTenantInspection && (
                              <span className="pc-badge-role inline-flex items-center gap-1">
                                <UserCheck className="w-3 h-3" />
                                Najemca
                              </span>
                            )}
                          </div>
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-slate-500">
                            <span className="flex items-center gap-1.5 flex-shrink-0">
                              <Home className="w-3.5 h-3.5 pc-icon" />
                              <span className="font-medium text-slate-700 truncate">{formatPropertyAddress()}</span>
                            </span>
                            <span className="flex items-center gap-1.5 flex-shrink-0">
                              <User className="w-3.5 h-3.5 text-violet-500" />
                              <span className="truncate">{tenantName}</span>
                            </span>
                            <span className="flex items-center gap-1.5 flex-shrink-0">
                              <Calendar className="w-3.5 h-3.5 text-slate-400" />
                              {new Date(inspection.date).toLocaleDateString('pl-PL')}
                            </span>
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-slate-800  transition-all flex-shrink-0" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
  );
}

// Stat Card Component
function StatCard({ icon, title, value, subtitle, small = false, onClick, clickable = false }) {
  const baseClasses = "bg-white rounded-2xl p-6 border border-slate-200/50 shadow-sm hover:shadow-md transition-all duration-300";
  const clickableClasses = clickable ? "cursor-pointer hover:scale-105" : "";

  return (
    <div
      className={`${baseClasses} ${clickableClasses}`}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="pc-stat-icon">
          <div className="pc-stat-icon-inner">
            {icon}
          </div>
        </div>
      </div>
      <p className="text-sm text-slate-500 mb-1">{title}</p>
      <p className={`font-bold text-slate-800 ${small ? 'text-xl' : 'text-3xl'}`}>{value}</p>
      {subtitle && (
        <p className="text-xs text-slate-400 mt-1">{subtitle}</p>
      )}
    </div>
  );
}

// Property Card Component
function PropertyCard({ property, inspections, isLocked = false, role = 'owner', invitation = null, onNewInspection, onView, onCompare, onDelete, onShowUpgradeModal, onInviteTenant }) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const lastInspection = inspections.sort((a, b) => new Date(b.date) - new Date(a.date))[0];

  // Get photo URL
  const { url: photoUrl } = usePropertyPhotoUrl(property.primary_photo_path);

  // Get rental status info
  const rentalStatus = RENTAL_STATUS[property.rental_status] || RENTAL_STATUS.available;

  // Determine if this is tenant view
  const isTenant = role === 'tenant';

  // Format address
  const formatAddress = () => {
    // If new format (structured)
    if (property.street && property.city) {
      const parts = [];
      let addressLine = property.street;
      if (property.building_number) addressLine += ` ${property.building_number}`;
      if (property.apartment_number) addressLine += `/${property.apartment_number}`;
      parts.push(addressLine);
      if (property.city) parts.push(property.city);
      return parts.join(', ');
    }
    // Fallback to old format
    return property.address || 'Brak adresu';
  };

  // Handle click when locked
  const handleLockedClick = (e) => {
    if (isLocked) {
      e.preventDefault();
      e.stopPropagation();
      onShowUpgradeModal?.();
    }
  };

  return (
    <div className={`bg-white rounded-2xl border border-slate-200/50 shadow-sm overflow-hidden hover:shadow-md hover:scale-105 transition-all duration-300 group relative ${isLocked ? 'opacity-60' : ''}`}>
      {/* Locked overlay */}
      {isLocked && (
        <div
          className="absolute inset-0 z-10 cursor-pointer"
          onClick={handleLockedClick}
        >
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20">
            <span className="px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-full text-xs font-bold shadow-lg backdrop-blur-sm flex items-center gap-2">
              <span className="text-lg">🔒</span>
              PLAN PRO
            </span>
          </div>
        </div>
      )}

      {/* Photo section */}
      <div
        className={`h-48 overflow-hidden relative bg-gradient-to-br from-violet-100 to-purple-200 ${isLocked ? 'cursor-default grayscale' : 'cursor-pointer'}`}
        onClick={isLocked ? handleLockedClick : () => onView(property)}
        role={isLocked ? undefined : "button"}
        tabIndex={isLocked ? undefined : 0}
        onKeyDown={isLocked ? undefined : (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onView(property);
          }
        }}
      >
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={formatAddress()}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Home className="w-16 h-16 text-slate-300" />
          </div>
        )}

        {/* Status badge OR Tenant badge */}
        <div className="absolute top-3 right-3">
          {isTenant ? (
            <span className="px-3 py-1 rounded-full text-xs font-medium text-white backdrop-blur-sm bg-blue-600/90 flex items-center gap-1.5">
              <UserCheck className="w-3.5 h-3.5" />
              Najemca
            </span>
          ) : (
            <span
              className="px-3 py-1 rounded-full text-xs font-medium text-white backdrop-blur-sm"
              style={{ backgroundColor: rentalStatus.color + 'CC' }}
            >
              {rentalStatus.label}
            </span>
          )}
        </div>

        {/* Delete button - only for owners */}
        {!isTenant && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (isLocked) {
                handleLockedClick(e);
              } else {
                setShowDeleteDialog(true);
              }
            }}
            className={`absolute top-3 left-3 p-2 bg-white/90 backdrop-blur-sm rounded-lg text-slate-400 hover:text-red-500 hover:bg-white transition-all z-20 ${isLocked ? 'cursor-not-allowed' : ''}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        {/* Address */}
        <h3 className="text-lg font-bold text-slate-800 mb-1 group-hover:text-slate-800 transition-colors">
          {formatAddress()}
        </h3>

        {/* Type and additional info */}
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
          <span>{getPropertyTypeLabel(property.type)}</span>
          {property.total_area && (
            <>
              <span>•</span>
              <span>{property.total_area} m²</span>
            </>
          )}
          {property.has_garage && (
            <>
              <span>•</span>
              <span>🚗</span>
            </>
          )}
          {property.has_storage_room && (
            <>
              <span>•</span>
              <span>📦</span>
            </>
          )}
        </div>

        {/* Inspections info */}
        <div className="flex items-center justify-between text-sm text-slate-500 mb-4 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            <span>{inspections.length} {inspections.length === 1 ? 'inspekcja' : 'inspekcji'}</span>
          </div>
          {lastInspection && (
            <span className="text-xs">
              Ostatnia: {new Date(lastInspection.date).toLocaleDateString('pl-PL')}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          {isTenant ? (
            // Tenant view - only view details and add periodic inspection
            <>
              <button
                onClick={() => onView(property)}
                className="flex-1 pc-btn-ghost text-sm flex items-center justify-center gap-2"
              >
                <Eye className="w-4 h-4" />
                Szczegóły
              </button>
              {invitation?.permissions?.upload_photos && (
                <button
                  onClick={() => onNewInspection(property)}
                  className="pc-btn-icon-primary"
                  title="Dodaj inspekcję okresową"
                >
                  <Plus className="w-4 h-4" />
                </button>
              )}
            </>
          ) : (
            // Owner view - all actions
            <>
              <button
                onClick={isLocked ? handleLockedClick : () => onView(property)}
                disabled={isLocked}
                className={`flex-1 pc-btn-ghost text-sm flex items-center justify-center gap-2 ${isLocked ? 'cursor-not-allowed opacity-50' : ''}`}
              >
                <Eye className="w-4 h-4" />
                Szczegóły
              </button>
              <button
                onClick={isLocked ? handleLockedClick : () => onNewInspection(property)}
                disabled={isLocked}
                className={`pc-btn-icon-primary ${isLocked ? 'cursor-not-allowed opacity-50' : ''}`}
                title="Dodaj inspekcję"
              >
                <Plus className="w-4 h-4" />
              </button>
              {inspections.length >= 2 && (
                <button
                  onClick={isLocked ? handleLockedClick : () => onCompare(property)}
                  disabled={isLocked}
                  className={`pc-btn-icon-secondary ${isLocked ? 'cursor-not-allowed opacity-50' : ''}`}
                  title="Porównaj inspekcje"
                >
                  <GitCompare className="w-4 h-4" />
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Usuń nieruchomość"
        message={`Czy na pewno chcesz usunąć nieruchomość "${formatAddress()}"? Wszystkie powiązane inspekcje również zostaną usunięte. Ta operacja jest nieodwracalna.`}
        confirmText="Usuń"
        cancelText="Anuluj"
        variant="danger"
        onConfirm={() => {
          setShowDeleteDialog(false);
          onDelete(property.id);
        }}
        onCancel={() => setShowDeleteDialog(false)}
      />
    </div>
  );
}

// New Property Form
function NewPropertyForm({ onSave, onUpdate, onCancel, property = null, showAlert, onUpgrade }) {
  const isEditMode = !!property;

  const [formData, setFormData] = useState({
    // Basic
    type: property?.type || '2-bedroom',

    // Address
    street: property?.street || '',
    buildingNumber: property?.building_number || '',
    apartmentNumber: property?.apartment_number || '',
    city: property?.city || '',
    postalCode: property?.postal_code || '',
    countryCode: property?.country_code || 'PL',

    // Property attributes
    hasGarage: property?.has_garage || false,
    hasStorageRoom: property?.has_storage_room || false,
    totalArea: property?.total_area || '',
    floorNumber: property?.floor_number || '',

    // Rental status (automatically synced from lease)
    rentalStatus: property?.rental_status || 'available',

    // Files
    primaryPhoto: null,
  });

  const { url: existingPhotoUrl } = usePropertyPhotoUrl(property?.primary_photo_path);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Track if form has changes
  useEffect(() => {
    const hasData =
      formData.type !== 'studio' || // Default type changed
      formData.street ||
      formData.building_number ||
      formData.apartment_number ||
      formData.postal_code ||
      formData.city ||
      formData.country_code !== 'PL' || // Default country changed
      formData.surface_area ||
      formData.floor ||
      formData.rental_status !== 'available' || // Default status changed
      formData.has_garage ||
      formData.has_storage_room ||
      photoPreview || // Photo was added
      existingPhotoUrl; // Editing with existing photo
    setHasChanges(hasData);
  }, [formData, photoPreview, existingPhotoUrl]);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, primaryPhoto: file });
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      let photoPath = property?.primary_photo_path || null;

      // Create property data object first
      const propertyData = {
        type: formData.type,
        street: formData.street,
        buildingNumber: formData.buildingNumber,
        apartmentNumber: formData.apartmentNumber,
        city: formData.city,
        postalCode: formData.postalCode,
        countryCode: formData.countryCode,
        hasGarage: formData.hasGarage,
        hasStorageRoom: formData.hasStorageRoom,
        totalArea: formData.totalArea || null,
        floorNumber: formData.floorNumber || null,
        rentalStatus: formData.rentalStatus,
      };

      // If creating new property, save first to get ID
      if (!isEditMode) {
        const result = await onSave(propertyData);

        // Get the created property ID from result
        const propertyId = result?.id;

        if (!propertyId) {
          throw new Error('Property ID not returned after creation');
        }

        // Upload photo if exists
        if (formData.primaryPhoto) {
          const photoResult = await uploadPropertyPhoto(formData.primaryPhoto, propertyId);
          photoPath = photoResult.path;
        }

        // Update property with file path if photo was uploaded
        if (photoPath && onUpdate) {
          await onUpdate(propertyId, {
            ...propertyData,
            primaryPhotoPath: photoPath,
          });
        }
      } else {
        // Edit mode - upload new photo if changed
        if (formData.primaryPhoto) {
          const photoResult = await uploadPropertyPhoto(formData.primaryPhoto, property.id);
          photoPath = photoResult.path;
        }

        // Save with updated path
        await onSave({
          ...propertyData,
          primaryPhotoPath: photoPath,
        });
      }
    } catch (error) {
      console.error('Error saving property:', error);
      showAlert(error.message, 'Błąd podczas zapisywania nieruchomości', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      setShowCancelDialog(true);
    } else {
      onCancel();
    }
  };

  return (
    <Layout onUpgrade={onUpgrade}>
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Back Button */}
        <button
          onClick={handleCancel}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">Powrót do panelu</span>
        </button>

        <div className="bg-white rounded-2xl p-8 border border-slate-200/50 shadow-xl">
          <h2 className="text-2xl font-bold text-slate-800 mb-6">
            {isEditMode ? 'Edytuj nieruchomość' : 'Dodaj nieruchomość'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* SEKCJA 1: Podstawowe informacje */}
            <div className="border-b border-slate-200 pb-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Podstawowe informacje</h3>

              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">Typ nieruchomości *</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all"
                >
                  <option value="studio">Kawalerka</option>
                  <option value="1-bedroom">Mieszkanie 2 pokojowe</option>
                  <option value="2-bedroom">Mieszkanie 3 pokojowe</option>
                  <option value="3-bedroom">Mieszkanie 4 pokojowe</option>
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Powierzchnia (m²)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.totalArea}
                    onChange={(e) => setFormData({ ...formData, totalArea: e.target.value })}
                    placeholder="50.5"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Piętro</label>
                  <input
                    type="number"
                    value={formData.floorNumber}
                    onChange={(e) => setFormData({ ...formData, floorNumber: e.target.value })}
                    placeholder="2"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all"
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.hasGarage}
                    onChange={(e) => setFormData({ ...formData, hasGarage: e.target.checked })}
                    className="w-5 h-5 text-violet-600 rounded focus:ring-2 focus:ring-violet-500"
                  />
                  <span className="text-sm text-slate-700">Garaż</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.hasStorageRoom}
                    onChange={(e) => setFormData({ ...formData, hasStorageRoom: e.target.checked })}
                    className="w-5 h-5 text-violet-600 rounded focus:ring-2 focus:ring-violet-500"
                  />
                  <span className="text-sm text-slate-700">Komórka lokatorska</span>
                </label>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">Zdjęcie nieruchomości</label>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  {(photoPreview || existingPhotoUrl) && (
                    <img
                      src={photoPreview || existingPhotoUrl}
                      alt="Podgląd"
                      className="w-full sm:w-32 h-48 sm:h-32 object-cover rounded-xl border border-slate-200"
                    />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="w-full sm:flex-1 text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">JPG, PNG lub WEBP. Maksymalnie 5MB.</p>
              </div>
            </div>

            {/* SEKCJA 2: Adres */}
            <div className="border-b border-slate-200 pb-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Adres</h3>

              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Ulica *</label>
                  <input
                    type="text"
                    value={formData.street}
                    onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                    placeholder="ul. Przykładowa"
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Numer *</label>
                  <input
                    type="text"
                    value={formData.buildingNumber}
                    onChange={(e) => setFormData({ ...formData, buildingNumber: e.target.value })}
                    placeholder="12"
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">Numer mieszkania</label>
                <input
                  type="text"
                  value={formData.apartmentNumber}
                  onChange={(e) => setFormData({ ...formData, apartmentNumber: e.target.value })}
                  placeholder="5"
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Kod pocztowy *</label>
                  <input
                    type="text"
                    value={formData.postalCode}
                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                    placeholder="00-000"
                    pattern="[0-9]{2}-[0-9]{3}"
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Miasto *</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Warszawa"
                    required
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Kraj *</label>
                  <CountrySelect
                    value={formData.countryCode}
                    onChange={(code) => setFormData({ ...formData, countryCode: code })}
                  />
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={handleCancel}
                className="flex-1 px-6 py-3 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-all duration-300 font-medium"
                disabled={uploading}
              >
                Anuluj
              </button>
              <button
                type="submit"
                disabled={uploading}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl hover:shadow-lg hover:shadow-violet-500/30 transition-all duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? 'Zapisuję...' : (isEditMode ? 'Zaktualizuj' : 'Zapisz nieruchomość')}
              </button>
            </div>
          </form>

          {/* Confirm Dialog */}
          <ConfirmDialog
            isOpen={showCancelDialog}
            title="Anulować dodawanie?"
            message="Wprowadzone dane zostaną utracone. Czy na pewno chcesz anulować?"
            confirmText="Tak, anuluj"
            cancelText="Kontynuuj edycję"
            variant="danger"
            onConfirm={() => {
              setShowCancelDialog(false);
              onCancel();
            }}
            onCancel={() => setShowCancelDialog(false)}
          />
        </div>
      </div>
    </Layout>
  );
}

// Select Inspection Type
function SelectInspectionType({ property, profile, inspections, onSelect, onCancel, onShowUpgradeModal }) {
  // Detect user role for this property
  const { isOwner, isTenant, permissions } = usePropertyRole(property.id, property);

  // Determine available types based on role
  const availableTypes = isTenant && !isOwner
    ? ['periodic'] // Tenants can only create periodic inspections
    : ['move-in', 'periodic', 'move-out']; // Owners can create all types

  const handleTypeSelect = (type) => {
    if (!profile) {
      return;
    }

    // For tenants, check permissions
    if (isTenant && !isOwner) {
      if (!permissions?.upload_photos) {
        onShowUpgradeModal('Brak uprawnień', 'Nie masz uprawnień do dodawania inspekcji dla tej nieruchomości.');
        return;
      }
      if (type !== 'periodic') {
        onShowUpgradeModal('Niedozwolony typ', 'Jako najemca możesz tworzyć tylko inspekcje okresowe.');
        return;
      }
      // Tenant permission OK, proceed
      onSelect(type);
      return;
    }

    // For owners, check subscription limits
    const typePermission = canCreateInspection(profile, type);

    if (!typePermission.allowed) {
      if (typePermission.requiresUpgrade) {
        onShowUpgradeModal('Inspekcja okresowa', typePermission.reason);
      }
      return;
    }

    // Check per-property inspection limit
    const propertyInspectionCount = inspections.filter(
      i => (i.property_id || i.propertyId) === property.id
    ).length;

    const countPermission = canCreateInspectionForProperty(profile, propertyInspectionCount);

    if (!countPermission.allowed) {
      if (countPermission.requiresUpgrade) {
        onShowUpgradeModal('Więcej inspekcji', countPermission.reason);
      }
      return;
    }

    // All checks passed, proceed with inspection
    onSelect(type);
  };

  return (
    <div className="max-w-2xl mx-auto px-6 py-8">
      {/* Back Button - OUTSIDE card on background */}
      <button
        onClick={onCancel}
        className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors group"
      >
        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
        <span className="font-medium">Powrót do panelu</span>
      </button>

      <div className="bg-white rounded-2xl p-8 border border-slate-200/50 shadow-xl">
        <div className="mb-6">
          {/* Tenant badge */}
          {isTenant && !isOwner && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium mb-3">
              <UserCheck className="w-3.5 h-3.5" />
              Tworzenie inspekcji jako najemca
            </div>
          )}
          <h2 className="text-2xl font-bold text-slate-800">Nowa inspekcja</h2>
          <p className="text-sm text-slate-500 mt-1">{formatPropertyAddress(property)}</p>
        </div>

        <p className="text-slate-600 mb-6">
          {isTenant && !isOwner
            ? 'Jako najemca możesz dodawać inspekcje okresowe:'
            : 'Wybierz typ inspekcji:'}
        </p>

          <div className="space-y-3">
            {Object.entries(INSPECTION_TYPES)
              .filter(([key]) => availableTypes.includes(key))
              .map(([key, { label, color }]) => (
              <button
                key={key}
                onClick={() => handleTypeSelect(key)}
                className="w-full p-5 border border-slate-200 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 text-left group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-slate-100">
                      <FileText className="w-6 h-6 text-slate-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800 group-hover:text-slate-800 transition-colors">{label}</h3>
                      <p className="text-sm text-slate-500">
                        {key === 'move-in' && 'Dokumentacja stanu przy wprowadzeniu najemcy'}
                        {key === 'periodic' && 'Kontrola okresowa stanu nieruchomości'}
                        {key === 'move-out' && 'Inspekcja przy wyprowadzce najemcy'}
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-slate-800  transition-all" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

// Inspection Wizard
function InspectionWizard({ inspection, step, isSaving, profile, user, refreshProfile, onNext, onPrev, onChange, onSave, onCancel, onUpgrade }) {
  const totalSteps = inspection.rooms.length + 2; // Basic info + rooms + summary

  // Scroll to top whenever step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  if (step === 0) {
    return (
      <WizardBasicInfo
        inspection={inspection}
        onChange={onChange}
        onNext={onNext}
        onCancel={onCancel}
        onUpgrade={onUpgrade}
      />
    );
  }

  if (step <= inspection.rooms.length) {
    return (
      <WizardRoomInspection
        inspection={inspection}
        roomIndex={step - 1}
        profile={profile}
        user={user}
        refreshProfile={refreshProfile}
        onChange={onChange}
        onNext={onNext}
        onPrev={onPrev}
        onCancel={onCancel}
        onUpgrade={onUpgrade}
      />
    );
  }

  return (
    <WizardSummary
      inspection={inspection}
      onChange={onChange}
      onSave={onSave}
      onPrev={onPrev}
      onCancel={onCancel}
      isSaving={isSaving}
      onUpgrade={onUpgrade}
    />
  );
}

// Wizard Basic Info
function WizardBasicInfo({ inspection, onChange, onNext, onCancel, onUpgrade }) {
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  // Get leases for property
  const { leases, activeLease } = useLeases(inspection.propertyId);

  // Auto-set leaseId from activeLease if not already set
  useEffect(() => {
    if (activeLease && !inspection.leaseId) {
      onChange({ ...inspection, leaseId: activeLease.id, tenant: activeLease.tenant_name });
    }
  }, [activeLease?.id]);

  // Format address helper
  const formatPropertyAddress = () => {
    const property = inspection.property;
    // If new format (structured)
    if (property.street && property.city) {
      const parts = [];
      let addressLine = property.street;
      if (property.building_number) addressLine += ` ${property.building_number}`;
      if (property.apartment_number) addressLine += `/${property.apartment_number}`;
      parts.push(addressLine);
      if (property.city) parts.push(property.city);
      return parts.join(', ');
    }
    // Fallback to old format
    return property.address || 'Brak adresu';
  };

  const handleCancel = () => {
    setShowCancelDialog(true);
  };

  return (
    <Layout onUpgrade={onUpgrade}>
      <div className="max-w-2xl mx-auto px-6 py-8">
        {/* Back Button - OUTSIDE card on background */}
        <button
          onClick={handleCancel}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">Powrót do panelu</span>
        </button>

        <div className="bg-white rounded-2xl p-8 border border-slate-200/50 shadow-xl">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-slate-800">Podstawowe informacje</h2>
            <p className="text-sm text-slate-500 mt-1">Krok 1 z {inspection.rooms.length + 2}</p>
          </div>

          <div className="space-y-5">
            <div className="p-4 bg-violet-50 border border-violet-200 rounded-xl">
              <div className="flex items-center gap-3">
                <Home className="w-5 h-5 text-violet-600 flex-shrink-0" />
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-violet-900">{formatPropertyAddress()}</h3>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-violet-200 text-violet-800">
                    {INSPECTION_TYPES[inspection.type].label}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Data inspekcji *</label>
              <input
                type="date"
                value={inspection.date}
                onChange={(e) => onChange({ ...inspection, date: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Osoba przeprowadzająca *</label>
              <input
                type="text"
                value={inspection.inspector}
                onChange={(e) => onChange({ ...inspection, inspector: e.target.value })}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all"
                placeholder="Maria Nowak (właściciel)"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Najemca / Umowa najmu
              </label>
              {leases.length > 0 ? (
                <select
                  value={inspection.leaseId || activeLease?.id || ''}
                  onChange={(e) => {
                    const selectedLeaseId = e.target.value;
                    const selectedLease = leases.find(l => l.id === selectedLeaseId);
                    onChange({
                      ...inspection,
                      leaseId: selectedLeaseId || null,
                      tenant: selectedLease ? selectedLease.tenant_name : ''
                    });
                  }}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all"
                >
                  <option value="">Brak umowy (inspekcja niezależna)</option>
                  {leases.map(lease => (
                    <option key={lease.id} value={lease.id}>
                      {lease.tenant_name} - {new Date(lease.start_date).toLocaleDateString('pl-PL')}
                      {lease.end_date ? ` do ${new Date(lease.end_date).toLocaleDateString('pl-PL')}` : ' (nieokreślona)'}
                      {' '}- {lease.status === 'active' ? '✓ Aktywna' : lease.status === 'ended' ? '○ Zakończona' : '✕ Rozwiązana'}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-600">
                  Brak umów najmu dla tej nieruchomości. Inspekcja będzie niezależna.
                </div>
              )}
              <p className="text-xs text-slate-500 mt-1">
                {leases.length > 0
                  ? 'Wybierz umowę najmu lub pozostaw puste dla inspekcji niezależnej (np. pre-rental)'
                  : 'Możesz dodać umowę najmu w szczegółach nieruchomości'
                }
              </p>
            </div>

            <button
              onClick={onNext}
              disabled={!inspection.inspector}
              className="w-full px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl hover:shadow-lg hover:shadow-violet-500/30 transition-all duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              Dalej
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Confirmation Dialog */}
          <ConfirmDialog
            isOpen={showCancelDialog}
            title="Anulować inspekcję?"
            message="Wprowadzone dane zostaną utracone. Czy na pewno chcesz przerwać inspekcję?"
            confirmText="Tak, anuluj"
            cancelText="Kontynuuj inspekcję"
            variant="danger"
            onConfirm={() => {
              setShowCancelDialog(false);
              onCancel();
            }}
            onCancel={() => setShowCancelDialog(false)}
          />
        </div>
      </div>
    </Layout>
  );
}

// Wizard Room Inspection
function WizardRoomInspection({ inspection, roomIndex, profile, user, refreshProfile, onChange, onNext, onPrev, onCancel, onUpgrade }) {
  const room = inspection.rooms[roomIndex];
  const totalRooms = inspection.rooms.length;
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState({
    isOpen: false,
    feature: '',
    message: '',
    upgradeContext: 'features', // 'features' or 'property_limit'
  });

  // Note: profile, user, refreshProfile are now passed as props instead of useAuth()

  // Calculate total photos in entire inspection (for Free plan)
  const getTotalPhotosCount = (inspection) => {
    return inspection.rooms.reduce((total, room) =>
      total + room.items.reduce((roomTotal, item) =>
        roomTotal + item.photos.length, 0
      ), 0
    );
  };

  // Quick check if can add photo (for UI state)
  const canAddPhotoQuick = (itemPhotos) => {
    if (!profile) return false;

    if (profile.subscription_plan === 'free') {
      return getTotalPhotosCount(inspection) < 5;
    } else {
      return itemPhotos.length < 4;
    }
  };

  const updateItem = (itemIndex, updates) => {
    const newRooms = [...inspection.rooms];
    newRooms[roomIndex].items[itemIndex] = { ...newRooms[roomIndex].items[itemIndex], ...updates };
    onChange({ ...inspection, rooms: newRooms });
  };

  const handlePhotoUpload = (itemIndex, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const item = room.items[itemIndex];

    // Validate photo limits
    const permission = canAddPhotoToItem(profile, inspection, item.photos);

    if (!permission.allowed) {
      if (permission.requiresUpgrade) {
        // Show upgrade modal for Free users
        setShowUpgradeModal({
          isOpen: true,
          feature: 'Więcej zdjęć w inspekcji',
          message: permission.reason,
        });
      } else {
        // Show alert for Pro users (per-item limit)
        alert(permission.reason);
      }
      // Reset file input
      e.target.value = '';
      return;
    }

    // Continue with existing logic
    const reader = new FileReader();
    reader.onloadend = () => {
      const newRooms = [...inspection.rooms];
      newRooms[roomIndex].items[itemIndex].photos = [
        ...newRooms[roomIndex].items[itemIndex].photos,
        reader.result
      ];
      onChange({ ...inspection, rooms: newRooms });
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = (itemIndex, photoIndex) => {
    const newRooms = [...inspection.rooms];
    newRooms[roomIndex].items[itemIndex].photos = newRooms[roomIndex].items[itemIndex].photos.filter((_, i) => i !== photoIndex);
    onChange({ ...inspection, rooms: newRooms });
  };

  const handleCancel = () => {
    setShowCancelDialog(true);
  };

  return (
    <Layout onUpgrade={onUpgrade}>
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-white rounded-2xl p-8 border border-slate-200/50 shadow-xl">
          {/* Back Button */}
          <button
            onClick={handleCancel}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">Powrót do panelu</span>
          </button>

          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-800">{room.roomName}</h2>
              <p className="text-sm text-slate-500 mt-1">
                Pomieszczenie {roomIndex + 1} z {totalRooms} • Krok {roomIndex + 2} z {totalRooms + 2}
              </p>
            </div>
            <div className="text-right">
              <div className="text-sm text-slate-500">{room.items.filter(i => i.condition !== 'good' || i.notes || i.photos.length > 0).length} / {room.items.length}</div>
              <div className="text-xs text-slate-400">sprawdzonych</div>
            </div>
          </div>

          {/* Progress */}
          <div className="mb-8">
            <div className="flex gap-1">
              {inspection.rooms.map((_, i) => (
                <div
                  key={i}
                  className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${
                    i < roomIndex ? 'bg-emerald-500' :
                    i === roomIndex ? 'bg-violet-500' :
                    'bg-slate-200'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Items */}
          <div className="space-y-6 mb-8">
            {room.items.map((item, itemIndex) => (
              <div key={itemIndex} className="p-5 border border-slate-200 rounded-xl hover:border-blue-300 transition-all">
                <h3 className="font-semibold text-slate-800 mb-4">{item.name}</h3>
                
                {/* Condition buttons */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                  {Object.entries(CONDITIONS).map(([key, { label, color, icon: Icon }]) => (
                    <button
                      key={key}
                      onClick={() => updateItem(itemIndex, { condition: key })}
                      className={`px-3 py-2 rounded-lg border-2 transition-all duration-300 text-sm font-medium ${
                        item.condition === key
                          ? 'border-current shadow-md scale-105'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      style={{
                        color: item.condition === key ? color : '#64748b',
                        backgroundColor: item.condition === key ? color + '15' : 'transparent',
                      }}
                    >
                      <div className="text-center">
                        <Icon className="w-5 h-5 mx-auto mb-1" />
                        <div>{label}</div>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Notes */}
                <textarea
                  value={item.notes}
                  onChange={(e) => updateItem(itemIndex, { notes: e.target.value })}
                  placeholder="Dodaj notatki (opcjonalnie)..."
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all resize-none text-sm"
                  rows="2"
                />

                {/* Photos */}
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Camera className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-600">Zdjęcia ({item.photos.length})</span>
                    </div>

                    {/* Photo counter/limit indicator */}
                    {profile?.subscription_plan === 'free' ? (
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        getTotalPhotosCount(inspection) >= 5
                          ? 'bg-red-100 text-red-700 font-medium'
                          : getTotalPhotosCount(inspection) >= 4
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-slate-100 text-slate-600'
                      }`}>
                        {getTotalPhotosCount(inspection)} / 5 (cała inspekcja)
                      </span>
                    ) : (
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        item.photos.length >= 4
                          ? 'bg-red-100 text-red-700 font-medium'
                          : item.photos.length >= 3
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-slate-100 text-slate-600'
                      }`}>
                        {item.photos.length} / 4
                      </span>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {item.photos.map((photo, photoIndex) => (
                      <div key={photoIndex} className="relative group">
                        <img
                          src={photo}
                          alt={`${item.name} - zdjęcie ${photoIndex + 1}`}
                          className="w-20 h-20 object-cover rounded-lg border border-slate-200"
                        />
                        <button
                          onClick={() => removePhoto(itemIndex, photoIndex)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}

                    {/* Upload button - disabled when limit reached */}
                    <label className={`w-20 h-20 border-2 border-dashed rounded-lg flex flex-col items-center justify-center transition-all ${
                      canAddPhotoQuick(item.photos)
                        ? 'border-slate-300 cursor-pointer hover:border-blue-400 hover:bg-violet-50'
                        : 'border-slate-200 bg-slate-50 cursor-not-allowed opacity-50'
                    }`}>
                      <Camera className="w-5 h-5 text-slate-400 mb-1" />
                      <span className="text-xs text-slate-500">
                        {canAddPhotoQuick(item.photos) ? 'Dodaj' : 'Limit'}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={(e) => handlePhotoUpload(itemIndex, e)}
                        disabled={!canAddPhotoQuick(item.photos)}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <button
              onClick={onPrev}
              className="w-full sm:w-auto px-6 py-3 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-all duration-300 font-medium flex items-center justify-center gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Wstecz
            </button>
            <button
              onClick={onNext}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl hover:shadow-lg hover:shadow-violet-500/30 transition-all duration-300 font-medium flex items-center justify-center gap-2"
            >
              {roomIndex === totalRooms - 1 ? 'Podsumowanie' : 'Następne pomieszczenie'}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Confirmation Dialog */}
          <ConfirmDialog
            isOpen={showCancelDialog}
            title="Anulować inspekcję?"
            message="Wprowadzone dane zostaną utracone. Czy na pewno chcesz przerwać inspekcję?"
            confirmText="Tak, anuluj"
            cancelText="Kontynuuj inspekcję"
            variant="danger"
            onConfirm={() => {
              setShowCancelDialog(false);
              onCancel();
            }}
            onCancel={() => setShowCancelDialog(false)}
          />

          {/* Upgrade Modal */}
          <UpgradeModal
            isOpen={showUpgradeModal.isOpen}
            onClose={() => setShowUpgradeModal({ isOpen: false, feature: '', message: '', upgradeContext: 'features' })}
            feature={showUpgradeModal.feature}
            message={showUpgradeModal.message}
            upgradeContext={showUpgradeModal.upgradeContext}
            profile={profile}
            user={user}
            refreshProfile={refreshProfile}
            onStartTrial={onUpgrade}
            onUpgradePaid={onUpgrade}
          />
        </div>
      </div>
    </Layout>
  );
}

// Wizard Summary
function WizardSummary({ inspection, onChange, onSave, onPrev, onCancel, isSaving, onUpgrade }) {
  const totalItems = inspection.rooms.reduce((sum, room) => sum + room.items.length, 0);
  const issuesCount = inspection.rooms.reduce((sum, room) =>
    sum + room.items.filter(item => item.condition === 'fair' || item.condition === 'poor').length, 0
  );
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const handleCancel = () => {
    setShowCancelDialog(true);
  };

  return (
    <Layout onUpgrade={onUpgrade}>
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-white rounded-2xl p-8 border border-slate-200/50 shadow-xl">
          {/* Back Button */}
          <button
            onClick={handleCancel}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">Powrót do panelu</span>
          </button>

          <div className="mb-6">
            <h2 className="text-2xl font-bold text-slate-800">Podsumowanie inspekcji</h2>
            <p className="text-sm text-slate-500 mt-1">Ostatni krok - przejrzyj i zapisz</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <div className="p-4 bg-violet-50 rounded-xl border border-violet-200">
              <div className="text-2xl font-bold text-violet-600 mb-1">{inspection.rooms.length}</div>
              <div className="text-sm text-violet-700">Pomieszczeń</div>
            </div>
            <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
              <div className="text-2xl font-bold text-emerald-600 mb-1">{totalItems}</div>
              <div className="text-sm text-emerald-700">Elementów</div>
            </div>
            <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
              <div className="text-2xl font-bold text-amber-600 mb-1">{issuesCount}</div>
              <div className="text-sm text-amber-700">Do uwagi</div>
            </div>
          </div>

          {/* Overall notes */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-slate-700 mb-2">Uwagi ogólne</label>
            <textarea
              value={inspection.overallNotes}
              onChange={(e) => onChange({ ...inspection, overallNotes: e.target.value })}
              placeholder="Dodaj ogólne uwagi dotyczące całej inspekcji..."
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-transparent outline-none transition-all resize-none"
              rows="4"
            />
          </div>

          {/* Room summary */}
          <div className="mb-8">
            <h3 className="font-semibold text-slate-800 mb-4">Przegląd pomieszczeń</h3>
            <div className="space-y-3">
              {inspection.rooms.map((room, index) => {
                const roomIssues = room.items.filter(item => item.condition === 'fair' || item.condition === 'poor');
                return (
                  <div key={index} className="p-4 border border-slate-200 rounded-xl">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                      <h4 className="font-medium text-slate-800">{room.roomName}</h4>
                      <div className="flex items-center gap-2">
                        {roomIssues.length > 0 ? (
                          <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium flex items-center gap-1 whitespace-nowrap">
                            <AlertCircle className="w-3 h-3" />
                            {roomIssues.length} do uwagi
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium flex items-center gap-1 whitespace-nowrap">
                            <CheckCircle className="w-3 h-3" />
                            Bez uwag
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <button
              onClick={onPrev}
              disabled={isSaving}
              className="w-full sm:w-auto px-6 py-3 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-all duration-300 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
              Wstecz
            </button>
            <button
              onClick={() => onSave(inspection)}
              disabled={isSaving}
              className="flex-1 pc-btn-primary px-6 py-3 flex items-center justify-center gap-2"
            >
              {isSaving ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Zapisywanie...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Zapisz inspekcję
                </>
              )}
            </button>
          </div>

          {/* Confirmation Dialog */}
          <ConfirmDialog
            isOpen={showCancelDialog}
            title="Anulować inspekcję?"
            message="Wprowadzone dane zostaną utracone. Czy na pewno chcesz przerwać inspekcję?"
            confirmText="Tak, anuluj"
            cancelText="Kontynuuj inspekcję"
            variant="danger"
            onConfirm={() => {
              setShowCancelDialog(false);
              onCancel();
            }}
            onCancel={() => setShowCancelDialog(false)}
          />
        </div>
      </div>
    </Layout>
  );
}

// Property Detail View
function PropertyDetail({ property, inspections, onBack, onViewInspection, onNewInspection, onEdit, onRefreshProperty, showAlert }) {
  // Debug: Log property data to diagnose empty view issue
  logger.log('[PropertyDetail] Rendering with property:', property);

  // Detect user's role for this property (owner or tenant)
  const { isOwner, isTenant, permissions } = usePropertyRole(property.id, property);
  const { profile } = useAuth();

  // Get photo URL
  const { url: photoUrl } = usePropertyPhotoUrl(property.primary_photo_path);

  // Get rental status info
  const rentalStatus = RENTAL_STATUS[property.rental_status] || RENTAL_STATUS.available;
  const currency = CURRENCIES[property.rental_currency] || CURRENCIES.PLN;

  // State for invite tenant modal
  const [showInviteModal, setShowInviteModal] = useState(false);

  // State for tenant invitations management
  const [activeInvitations, setActiveInvitations] = useState([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [revokeConfirm, setRevokeConfirm] = useState({ isOpen: false, invitationId: null, tenantEmail: '' });

  // Load active invitations for this property (owner only)
  useEffect(() => {
    if (!isOwner) return;

    async function loadInvitations() {
      try {
        setLoadingInvitations(true);
        const { getPropertyInvitations } = await import('./services/supabase/tenantInvitations');
        const invitations = await getPropertyInvitations(property.id);
        setActiveInvitations(invitations.filter(inv => inv.status === 'accepted'));
      } catch (error) {
        console.error('Error loading invitations:', error);
      } finally {
        setLoadingInvitations(false);
      }
    }

    loadInvitations();
  }, [property.id, isOwner]);

  const handleRevokeAccess = async () => {
    try {
      const { revokeInvitation } = await import('./services/supabase/tenantInvitations');
      await revokeInvitation(revokeConfirm.invitationId);

      // Refresh invitations list
      const { getPropertyInvitations } = await import('./services/supabase/tenantInvitations');
      const invitations = await getPropertyInvitations(property.id);
      setActiveInvitations(invitations.filter(inv => inv.status === 'accepted'));

      setRevokeConfirm({ isOpen: false, invitationId: null, tenantEmail: '' });
      showAlert && showAlert('Dostęp najemcy został cofnięty.', 'Sukces', 'success');
    } catch (error) {
      console.error('Error revoking invitation:', error);
      showAlert && showAlert('Błąd podczas cofania dostępu.', 'Błąd', 'error');
    }
  };

  // Leases management
  const {
    leases,
    activeLease,
    loading: leasesLoading,
    createLease,
    updateLease,
    terminateLease,
    archiveLease,
  } = useLeases(property.id);

  const [showLeaseForm, setShowLeaseForm] = useState(false);
  const [editingLease, setEditingLease] = useState(null);
  const [showArchivedLeases, setShowArchivedLeases] = useState(false);
  const [showReplaceLeaseDialog, setShowReplaceLeaseDialog] = useState(false);
  const [pendingLeaseData, setPendingLeaseData] = useState(null);

  // Filter leases based on archive toggle
  const visibleLeases = showArchivedLeases
    ? leases
    : leases.filter(l => l.status !== 'archived');

  // Format address
  const formatAddress = () => {
    const parts = [];
    if (property.street && property.building_number) {
      let addressLine = `${property.street} ${property.building_number}`;
      if (property.apartment_number) {
        addressLine += `/${property.apartment_number}`;
      }
      parts.push(addressLine);
    }
    if (property.postal_code && property.city) {
      parts.push(`${property.postal_code} ${property.city}`);
    } else if (property.city) {
      parts.push(property.city);
    }
    const address = parts.join(', ');
    return address || 'Brak adresu';
  };

  // Process lease save (handles file upload and save)
  const processSaveLease = async (leaseData) => {
    // Handle file upload if present
    let contractPath = leaseData.contractAttachmentPath;

    if (leaseData.contractAttachment) {
      try {
        const uploadResult = await uploadPropertyDocument(
          leaseData.contractAttachment,
          property.id
        );
        contractPath = uploadResult.path;
      } catch (error) {
        showAlert(error.message, 'Błąd uploadu dokumentu', 'error');
        return;
      }
    }

    const finalLeaseData = {
      ...leaseData,
      contractAttachmentPath: contractPath,
      status: leaseData.status || 'active', // Domyślnie aktywna
    };

    const result = editingLease
      ? await updateLease(editingLease.id, finalLeaseData)
      : await createLease(finalLeaseData);

    if (result.success) {
      setShowLeaseForm(false);
      setEditingLease(null);
      onRefreshProperty();
    } else {
      showAlert(result.error, 'Błąd zapisu umowy', 'error');
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      {/* Back Button - OUTSIDE card on background */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors group"
      >
        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
        <span className="font-medium">Powrót do panelu</span>
      </button>

      <div className="bg-white rounded-2xl p-8 border border-slate-200/50 shadow-xl mb-6">
        {/* Header with photo */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Photo */}
          <div className="h-64 overflow-hidden relative bg-gradient-to-br from-violet-100 to-purple-200 rounded-xl">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={formatAddress()}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Home className="w-16 h-16 text-slate-300" />
              </div>
            )}
            {/* Status badge on photo */}
            <div className="absolute top-3 right-3">
              <span
                className="px-3 py-1 rounded-full text-xs font-medium text-white backdrop-blur-sm"
                style={{ backgroundColor: rentalStatus.color + 'CC' }}
              >
                {rentalStatus.label}
              </span>
            </div>
          </div>

          {/* Property Info */}
          <div className="flex flex-col justify-between">
            <div>
              {/* Role badge for tenants */}
              {isTenant && !isOwner && (
                <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-medium mb-2">
                  <UserCheck className="w-3.5 h-3.5" />
                  Widok najemcy
                </div>
              )}

              <h2 className="text-2xl font-bold text-slate-800 mb-2">{formatAddress()}</h2>

              {/* Property details */}
              <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600 mb-4">
                <span className="font-medium">{getPropertyTypeLabel(property.type)}</span>
                {property.total_area && (
                  <>
                    <span>•</span>
                    <span>{property.total_area} m²</span>
                  </>
                )}
                {property.floor_number && (
                  <>
                    <span>•</span>
                    <span>Piętro {property.floor_number}</span>
                  </>
                )}
              </div>

              {/* Garage & Storage */}
              {(property.has_garage || property.has_storage_room) && (
                <div className="flex gap-2 mb-4">
                  {property.has_garage && (
                    <span className="px-2 py-1 bg-slate-100 rounded-lg text-xs text-slate-700">
                      🚗 Garaż
                    </span>
                  )}
                  {property.has_storage_room && (
                    <span className="px-2 py-1 bg-slate-100 rounded-lg text-xs text-slate-700">
                      📦 Komórka
                    </span>
                  )}
                </div>
              )}

            </div>

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 mt-4">
              {/* Edit button - owner only */}
              {isOwner && (
                <button
                  onClick={onEdit}
                  className="pc-btn-ghost px-5 py-2.5 flex items-center justify-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edytuj
                </button>
              )}

              {/* Add inspection button - PRIMARY CTA */}
              {(isOwner || (isTenant && permissions?.upload_photos)) && (
                <button
                  onClick={onNewInspection}
                  className="pc-btn-primary px-5 py-2.5 flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {isTenant && !isOwner ? 'Dodaj inspekcję okresową' : 'Dodaj inspekcję'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Leases Section - Owner only */}
        {isOwner && (
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <h3 className="text-lg font-semibold text-slate-800">
                  Najemcy ({visibleLeases.length}{leases.length !== visibleLeases.length ? ` z ${leases.length}` : ''})
                </h3>
              {leases.some(l => l.status === 'archived') && (
                <button
                  onClick={() => setShowArchivedLeases(!showArchivedLeases)}
                  className="text-sm text-slate-600 hover:text-slate-900 transition-colors text-left sm:text-center"
                >
                  {showArchivedLeases ? 'Ukryj archiwum' : 'Pokaż archiwum'}
                </button>
              )}
            </div>
            <button
              onClick={() => setShowLeaseForm(true)}
              disabled={activeLease && !editingLease}
              className="pc-btn-secondary px-5 py-2.5 flex items-center justify-center sm:justify-start gap-2 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
            >
              <Plus className="w-4 h-4" />
              Dodaj umowę
            </button>
          </div>

          {leasesLoading ? (
            <div className="text-center py-8 text-slate-500">Ładowanie...</div>
          ) : visibleLeases.length === 0 && !showArchivedLeases && leases.length > 0 ? (
            <div className="text-center py-8 border border-dashed border-slate-300 rounded-xl">
              <p className="text-slate-600 mb-2">Wszystkie umowy są zarchiwizowane</p>
              <button
                onClick={() => setShowArchivedLeases(true)}
                className="text-sm text-slate-600 hover:text-slate-900 font-medium"
              >
                Pokaż archiwum
              </button>
            </div>
          ) : visibleLeases.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-slate-300 rounded-xl">
              <User className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 mb-4">Brak zarejestrowanych umów najmu</p>
              <button
                onClick={() => setShowLeaseForm(true)}
                className="pc-btn-secondary px-6 py-3"
              >
                Dodaj pierwszą umowę
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {visibleLeases.map(lease => (
                <LeaseCard
                  key={lease.id}
                  lease={lease}
                  isActive={activeLease?.id === lease.id}
                  onEdit={(lease) => {
                    setEditingLease(lease);
                    setShowLeaseForm(true);
                  }}
                  onTerminate={async (reason, date) => {
                    const { revokeAllInvitationsForProperty } = await import('./services/supabase/tenantInvitations');
                    await revokeAllInvitationsForProperty(property.id);
                    await terminateLease(lease.id, reason, date);
                    onRefreshProperty();
                  }}
                  onArchive={async () => {
                    const { revokeAllInvitationsForProperty } = await import('./services/supabase/tenantInvitations');
                    await revokeAllInvitationsForProperty(property.id);
                    await archiveLease(lease.id);
                  }}
                  onInviteTenant={activeLease?.id === lease.id && isOwner && profile?.subscription_plan === 'pro' ? () => setShowInviteModal(true) : null}
                />
              ))}
            </div>
          )}
        </div>
        )}

        {/* Active Tenant Invitations - Owner only */}
        {isOwner && activeInvitations.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">
              Aktywni najemcy z dostępem ({activeInvitations.length})
            </h3>
            <div className="space-y-4">
              {activeInvitations.map((invitation) => (
                <div
                  key={invitation.id}
                  className="border border-slate-200 rounded-xl p-5 bg-white"
                >
                  {/* Header with avatar and info */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-slate-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 text-lg truncate">{invitation.tenant_email}</p>
                      <div className="flex items-center gap-1 text-xs text-slate-500">
                        <Calendar className="w-3 h-3" />
                        Dodano: {new Date(invitation.accepted_at).toLocaleDateString('pl-PL')}
                      </div>
                    </div>
                  </div>

                  {/* Divider and actions */}
                  <div className="mt-4 pt-4 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-2 flex-wrap justify-center sm:justify-start">
                      {invitation.permissions?.view_inspections && (
                        <span className="inline-flex items-center justify-center px-2 py-0.5 bg-slate-100 text-slate-700 text-xs font-medium rounded-full">
                          Może przeglądać
                        </span>
                      )}
                      {invitation.permissions?.upload_photos && (
                        <span className="inline-flex items-center justify-center px-2 py-0.5 bg-slate-100 text-slate-700 text-xs font-medium rounded-full">
                          Może dodawać inspekcje
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => setRevokeConfirm({ isOpen: true, invitationId: invitation.id, tenantEmail: invitation.tenant_email })}
                      className="px-4 py-2 text-sm text-rose-600 rounded-lg hover:bg-rose-50 transition-colors w-full sm:w-auto"
                    >
                      Cofnij dostęp
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-4">
            Historia inspekcji ({inspections.length})
          </h3>
          {inspections.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center border border-slate-200/50">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 mb-4">Brak inspekcji dla tej nieruchomości</p>
              <button
                onClick={onNewInspection}
                className="pc-btn-primary px-6 py-3 inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Dodaj pierwszą inspekcję
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {inspections.sort((a, b) => new Date(b.date) - new Date(a.date)).map(inspection => (
                <div
                  key={inspection.id}
                  onClick={() => onViewInspection(inspection)}
                  className="bg-white rounded-2xl p-5 border border-slate-200/50 shadow-sm hover:shadow-md hover:scale-105 transition-all duration-300 cursor-pointer group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                        <FileText className="w-6 h-6 text-slate-400" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-800 mb-1">
                          {INSPECTION_TYPES[inspection.type].label}
                        </h4>
                        <p className="text-sm text-slate-500 flex items-center gap-3">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {new Date(inspection.date).toLocaleDateString('pl-PL')}
                          </span>
                          <span className="flex items-center gap-1">
                            <User className="w-3.5 h-3.5" />
                            {inspection.inspector}
                          </span>
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 transition-all" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal formularza dodawania/edycji umowy */}
      {showLeaseForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-slate-800 mb-6">
              {editingLease ? 'Edytuj umowę najmu' : 'Nowa umowa najmu'}
            </h2>
            <LeaseForm
              propertyId={property.id}
              lease={editingLease}
              onSave={async (leaseData) => {
                // Walidacja: sprawdź czy nie ma już aktywnej umowy (tylko dla nowych umów)
                if (!editingLease && activeLease) {
                  // Zapisz dane i pokaż dialog
                  setPendingLeaseData(leaseData);
                  setShowReplaceLeaseDialog(true);
                  return;
                }

                // Zapisz umowę
                await processSaveLease(leaseData);
              }}
              onCancel={() => {
                setShowLeaseForm(false);
                setEditingLease(null);
              }}
            />
          </div>
        </div>
      )}

      {/* Replace Lease Dialog */}
      <ConfirmDialog
        isOpen={showReplaceLeaseDialog}
        title="Zastąp aktywną umowę"
        message={`Istnieje już aktywna umowa dla ${activeLease?.tenant_name}.\n\nCzy chcesz rozwiązać obecną umowę i utworzyć nową?`}
        confirmText="Zastąp umowę"
        cancelText="Anuluj"
        variant="warning"
        onConfirm={async () => {
          setShowReplaceLeaseDialog(false);

          // Wycofaj dostęp dla najemców
          const { revokeAllInvitationsForProperty } = await import('./services/supabase/tenantInvitations');
          await revokeAllInvitationsForProperty(property.id);

          // Rozwiąż obecną umowę
          await terminateLease(activeLease.id, 'Zastąpiona nową umową', new Date().toISOString().split('T')[0]);

          // Zapisz nową umowę
          if (pendingLeaseData) {
            await processSaveLease(pendingLeaseData);
            setPendingLeaseData(null);
          }
        }}
        onCancel={() => {
          setShowReplaceLeaseDialog(false);
          setPendingLeaseData(null);
        }}
      />

      {/* Invite Tenant Modal */}
      <InviteTenantModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        property={property}
      />

      {/* Revoke Access Confirmation Modal */}
      <ConfirmDialog
        isOpen={revokeConfirm.isOpen}
        title="Cofnij dostęp najemcy"
        message={`Czy na pewno chcesz cofnąć dostęp dla ${revokeConfirm.tenantEmail} do tej nieruchomości?\n\nNajemca utraci możliwość przeglądania i dodawania inspekcji.`}
        confirmText="Cofnij dostęp"
        cancelText="Anuluj"
        variant="danger"
        onConfirm={handleRevokeAccess}
        onCancel={() => setRevokeConfirm({ isOpen: false, invitationId: null, tenantEmail: '' })}
      />
    </div>
  );
}

// Inspection View
function InspectionView({ inspection, property, onBack, onGeneratePDF, onDelete }) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [photoModal, setPhotoModal] = useState({
    isOpen: false,
    photoUrl: '',
    caption: '',
    allPhotos: [],
    currentIndex: 0
  });

  // Check if user is owner or tenant of this property
  const { isOwner, isTenant } = usePropertyRole(property?.id, property);

  const handlePhotoClick = (photoUrl, itemName, roomName, allItemPhotos = []) => {
    const currentIndex = allItemPhotos.indexOf(photoUrl);
    setPhotoModal({
      isOpen: true,
      photoUrl: photoUrl,
      caption: `${roomName} - ${itemName}`,
      allPhotos: allItemPhotos.length > 1 ? allItemPhotos : null,
      currentIndex: currentIndex >= 0 ? currentIndex : 0
    });
  };

  const handlePhotoNavigate = (newIndex) => {
    if (photoModal.allPhotos && newIndex >= 0 && newIndex < photoModal.allPhotos.length) {
      setPhotoModal({
        ...photoModal,
        photoUrl: photoModal.allPhotos[newIndex],
        currentIndex: newIndex
      });
    }
  };

  // DEBUG: Sprawdź zdjęcia
  logger.log('🔍 InspectionView - inspection object:', inspection);
  inspection.rooms?.forEach((room, idx) => {
    room.items?.forEach((item, itemIdx) => {
      if (item.photos && item.photos.length > 0) {
        logger.log(`✅ Pokój ${idx} "${room.roomName}", element ${itemIdx} "${item.name}": ${item.photos.length} zdjęć`, item.photos);
      } else {
        logger.log(`❌ Pokój ${idx} "${room.roomName}", element ${itemIdx} "${item.name}": BRAK zdjęć`);
      }
    });
  });

  const issuesCount = inspection.rooms.reduce((sum, room) =>
    sum + room.items.filter(item => item.condition === 'fair' || item.condition === 'poor').length, 0
  );

  return (
    <>
      <div className="max-w-4xl mx-auto px-6 py-8">
      {/* Back Button - OUTSIDE card on background */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors group"
      >
        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
        <span className="font-medium">Powrót do panelu</span>
      </button>

      <div className="bg-white rounded-2xl p-8 border border-slate-200/50 shadow-xl mb-6">
        {/* Property and inspection type header with actions */}
        <div className="mb-6">
          <div className="p-4 bg-violet-50 border border-violet-200 rounded-xl">
            <div className="flex flex-col gap-3">
              {/* Address and chip */}
              <div className="flex items-center gap-3 flex-wrap">
                <Home className="w-5 h-5 text-violet-600 flex-shrink-0" />
                <h3 className="font-semibold text-violet-900 break-words flex-1 min-w-0">{formatPropertyAddress(property)}</h3>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-violet-200 text-violet-800 whitespace-nowrap">
                  {INSPECTION_TYPES[inspection.type].label}
                </span>
              </div>

              {/* Action buttons - vertical on mobile, horizontal on desktop */}
              <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                <button
                  onClick={() => onGeneratePDF(inspection)}
                  className="px-3 py-1.5 text-sm text-violet-600 rounded-lg hover:bg-violet-100 transition-colors w-full sm:w-auto"
                >
                  Pobierz PDF
                </button>
                {isOwner && (
                  <button
                    onClick={() => setShowDeleteDialog(true)}
                    className="px-3 py-1.5 text-sm text-rose-600 rounded-lg hover:bg-rose-100 transition-colors w-full sm:w-auto"
                  >
                    Usuń
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div className="p-4 bg-slate-50 rounded-xl">
              <div className="text-xs text-slate-500 mb-1">Data inspekcji</div>
              <div className="font-semibold text-slate-800">{new Date(inspection.date).toLocaleDateString('pl-PL')}</div>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl">
              <div className="text-xs text-slate-500 mb-1">Inspektor</div>
              <div className="font-semibold text-slate-800">{inspection.inspector}</div>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl">
              <div className="text-xs text-slate-500 mb-1">Najemca</div>
              <div className="font-semibold text-slate-800">{inspection.tenant || '-'}</div>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl">
              <div className="text-xs text-slate-500 mb-1">Uwagi</div>
              <div className="font-semibold text-slate-800 flex items-center gap-2">
                {issuesCount > 0 ? (
                  <>
                    <AlertCircle className="w-4 h-4 text-amber-500" />
                    {issuesCount} elementów
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    Bez uwag
                  </>
                )}
              </div>
            </div>
          </div>

          {inspection.overallNotes && (
            <div className="p-4 bg-violet-50 border border-violet-200 rounded-xl mb-6">
              <h3 className="text-sm font-semibold text-blue-900 mb-2">Uwagi ogólne</h3>
              <p className="text-sm text-blue-800">{inspection.overallNotes}</p>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {inspection.rooms.map((room, roomIndex) => (
            <div key={roomIndex} className="bg-white rounded-2xl p-6 border border-slate-200/50 shadow-sm">
              <h3 className="text-lg font-bold text-slate-800 mb-4">{room.roomName}</h3>
              <div className="space-y-4">
                {room.items.map((item, itemIndex) => (
                  <div key={itemIndex} className="pb-4 border-b border-slate-100 last:border-0">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-slate-700">{item.name}</h4>
                      <span
                        className="px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1"
                        style={{
                          backgroundColor: CONDITIONS[item.condition].color + '15',
                          color: CONDITIONS[item.condition].color,
                        }}
                      >
                        {React.createElement(CONDITIONS[item.condition].icon, { className: 'w-3 h-3' })}
                        {CONDITIONS[item.condition].label}
                      </span>
                    </div>
                    {item.notes && (
                      <p className="text-sm text-slate-600 mb-2">{item.notes}</p>
                    )}
                    {item.photos.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {item.photos.map((photo, photoIndex) => (
                          <img
                            key={photoIndex}
                            src={photo}
                            alt={`${item.name} - zdjęcie ${photoIndex + 1}`}
                            className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg border border-slate-200 cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => handlePhotoClick(photo, item.name, room.roomName, item.photos)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Usuń inspekcję?"
        message={`Czy na pewno chcesz usunąć inspekcję typu "${INSPECTION_TYPES[inspection.type].label}" z dnia ${new Date(inspection.date).toLocaleDateString('pl-PL')}?\n\nTa operacja jest nieodwracalna.`}
        confirmText="Usuń"
        cancelText="Anuluj"
        variant="danger"
        onConfirm={() => {
          setShowDeleteDialog(false);
          onDelete(inspection.id);
        }}
        onCancel={() => setShowDeleteDialog(false)}
      />

      {/* Photo Modal */}
      <PhotoModal
        isOpen={photoModal.isOpen}
        photoUrl={photoModal.photoUrl}
        caption={photoModal.caption}
        allPhotos={photoModal.allPhotos}
        currentIndex={photoModal.currentIndex}
        onNavigate={handlePhotoNavigate}
        onClose={() => setPhotoModal({ ...photoModal, isOpen: false })}
      />
    </>
  );
}

// Compare Select
function CompareSelect({ property, inspections, onCompare, onBack }) {
  const [selected, setSelected] = useState([null, null]);

  const handleSelect = (inspection, index) => {
    const newSelected = [...selected];
    newSelected[index] = inspection;
    setSelected(newSelected);
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      {/* Back Button - OUTSIDE card on background */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors group"
      >
        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
        <span className="font-medium">Powrót do panelu</span>
      </button>

      <div className="bg-white rounded-2xl p-8 border border-slate-200/50 shadow-xl">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-slate-800">Porównaj inspekcje</h2>
          <p className="text-sm text-slate-500 mt-1">{formatPropertyAddress(property)}</p>
        </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
            <div>
              <h3 className="font-semibold text-slate-700 mb-3 text-base md:text-lg">Inspekcja 1 (PRZED)</h3>
              <div className="space-y-2">
                {inspections.sort((a, b) => new Date(a.date) - new Date(b.date)).map(inspection => (
                  <button
                    key={inspection.id}
                    onClick={() => handleSelect(inspection, 0)}
                    className={`w-full p-4 border-2 rounded-xl text-left transition-all ${
                      selected[0]?.id === inspection.id
                        ? 'border-violet-500 bg-violet-50'
                        : 'border-slate-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="font-medium text-slate-800">{INSPECTION_TYPES[inspection.type].label}</div>
                    <div className="text-sm text-slate-500 mt-1">{new Date(inspection.date).toLocaleDateString('pl-PL')}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-slate-700 mb-3 text-base md:text-lg">Inspekcja 2 (PO)</h3>
              <div className="space-y-2">
                {inspections.sort((a, b) => new Date(a.date) - new Date(b.date)).map(inspection => (
                  <button
                    key={inspection.id}
                    onClick={() => handleSelect(inspection, 1)}
                    disabled={selected[0]?.id === inspection.id}
                    className={`w-full p-4 border-2 rounded-xl text-left transition-all ${
                      selected[1]?.id === inspection.id
                        ? 'border-emerald-500 bg-emerald-50'
                        : selected[0]?.id === inspection.id
                        ? 'border-slate-200 opacity-50 cursor-not-allowed'
                        : 'border-slate-200 hover:border-emerald-300'
                    }`}
                  >
                    <div className="font-medium text-slate-800">{INSPECTION_TYPES[inspection.type].label}</div>
                    <div className="text-sm text-slate-500 mt-1">{new Date(inspection.date).toLocaleDateString('pl-PL')}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <button
            onClick={() => onCompare(selected[0], selected[1])}
            disabled={!selected[0] || !selected[1]}
            className="w-full px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl hover:shadow-lg hover:shadow-violet-500/30 transition-all duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <GitCompare className="w-5 h-5" />
            Porównaj inspekcje
          </button>
        </div>
      </div>
    );
  }

// Compare View
function CompareView({ inspections, property, onBack, onGeneratePDF }) {
  const [insp1, insp2] = inspections;
  const [showOnlyDifferences, setShowOnlyDifferences] = useState(true);
  const [photoModal, setPhotoModal] = useState({
    isOpen: false,
    photoUrl: '',
    caption: '',
    allPhotos: [],
    currentIndex: 0
  });

  const handlePhotoClick = (photoUrl, itemName, roomName, allItemPhotos = [], side = '') => {
    const currentIndex = allItemPhotos.indexOf(photoUrl);
    setPhotoModal({
      isOpen: true,
      photoUrl: photoUrl,
      caption: `${roomName} - ${itemName}${side ? ` (${side})` : ''}`,
      allPhotos: allItemPhotos.length > 1 ? allItemPhotos : null,
      currentIndex: currentIndex >= 0 ? currentIndex : 0
    });
  };

  const handlePhotoNavigate = (newIndex) => {
    if (photoModal.allPhotos && newIndex >= 0 && newIndex < photoModal.allPhotos.length) {
      setPhotoModal({
        ...photoModal,
        photoUrl: photoModal.allPhotos[newIndex],
        currentIndex: newIndex
      });
    }
  };

  return (
    <>
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Back Button - OUTSIDE card on background */}
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">Powrót do panelu</span>
        </button>

        <div className="bg-white rounded-2xl p-8 border border-slate-200/50 shadow-xl mb-6">
          {/* Property header with actions */}
          <div className="p-4 bg-violet-50 border border-violet-200 rounded-xl mb-6">
            <div className="flex flex-col gap-3">
              {/* Address */}
              <div className="flex items-center gap-3">
                <Home className="w-5 h-5 text-violet-600 flex-shrink-0" />
                <h3 className="font-semibold text-violet-900 break-words">{formatPropertyAddress(property)}</h3>
              </div>

              {/* Toggle and PDF button - vertical on mobile, horizontal on desktop */}
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-sm font-medium text-slate-700">Tylko różnice</span>
                  <div
                    onClick={() => setShowOnlyDifferences(!showOnlyDifferences)}
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      showOnlyDifferences ? 'bg-violet-600' : 'bg-slate-300'
                    }`}
                  >
                    <div
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                        showOnlyDifferences ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </div>
                </label>
                <button
                  onClick={onGeneratePDF}
                  className="px-3 py-1.5 text-sm text-violet-600 rounded-lg hover:bg-violet-100 transition-colors w-full sm:w-auto"
                >
                  Pobierz PDF
                </button>
              </div>
            </div>
          </div>

          <h2 className="text-xl sm:text-2xl font-bold text-slate-800 mb-6">Porównanie inspekcji</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
            <div className="p-4 bg-violet-50 border border-violet-200 rounded-xl">
              <div className="text-xs text-violet-600 mb-1">PRZED</div>
              <div className="font-semibold text-blue-900">{INSPECTION_TYPES[insp1.type].label}</div>
              <div className="text-sm text-violet-700 mt-1">{new Date(insp1.date).toLocaleDateString('pl-PL')}</div>
            </div>
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
              <div className="text-xs text-emerald-600 mb-1">PO</div>
              <div className="font-semibold text-emerald-900">{INSPECTION_TYPES[insp2.type].label}</div>
              <div className="text-sm text-emerald-700 mt-1">{new Date(insp2.date).toLocaleDateString('pl-PL')}</div>
            </div>
          </div>

          {/* Overall notes from both inspections */}
          {(insp1.overallNotes || insp2.overallNotes) && (
            <div className="mb-6">
              <h3 className="text-lg font-bold text-slate-800 mb-4">Uwagi ogólne</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-4 bg-violet-50 border border-violet-200 rounded-xl">
                  <div className="text-xs text-violet-600 font-medium mb-2">PRZED</div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">
                    {insp1.overallNotes || <span className="text-slate-400 italic">Brak uwag</span>}
                  </p>
                </div>
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <div className="text-xs text-emerald-600 font-medium mb-2">PO</div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">
                    {insp2.overallNotes || <span className="text-slate-400 italic">Brak uwag</span>}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          {insp1.rooms.map((room1, roomIndex) => {
            const room2 = insp2.rooms[roomIndex];
            if (!room2) return null;

            // Check if room has any changed items (for filtering)
            const roomHasChanges = room1.items.some((item1, idx) => {
              const item2 = room2.items[idx];
              return item2 && item1.condition !== item2.condition;
            });

            // Skip room if showing only differences and room has no changes
            if (showOnlyDifferences && !roomHasChanges) return null;

            return (
              <div key={roomIndex} className="bg-white rounded-2xl p-6 border border-slate-200/50 shadow-sm">
                <h3 className="text-lg font-bold text-slate-800 mb-4">{room1.roomName}</h3>
                <div className="space-y-6">
                  {room1.items.map((item1, itemIndex) => {
                    const item2 = room2.items[itemIndex];
                    if (!item2) return null;

                    const hasChanged = item1.condition !== item2.condition;

                    // Filter: if showOnlyDifferences is true, skip items that haven't changed
                    if (showOnlyDifferences && !hasChanged) return null;

                    return (
                      <div key={itemIndex} className={`p-4 rounded-xl border-2 ${hasChanged ? 'border-amber-300 bg-amber-50' : 'border-slate-200'}`}>
                        <div className="flex items-center gap-2 mb-3">
                          <h4 className="font-semibold text-slate-800">{item1.name}</h4>
                          {hasChanged && (
                            <span className="px-2 py-1 bg-amber-200 text-amber-800 rounded-full text-xs font-medium">
                              Zmiana
                            </span>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <div className="text-xs text-violet-600 font-medium mb-2">PRZED</div>
                            <div
                              className="px-3 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-1"
                              style={{
                                backgroundColor: CONDITIONS[item1.condition].color + '15',
                                color: CONDITIONS[item1.condition].color,
                              }}
                            >
                              {React.createElement(CONDITIONS[item1.condition].icon, { className: 'w-4 h-4' })}
                              {CONDITIONS[item1.condition].label}
                            </div>
                            {item1.notes && <p className="text-sm text-slate-600 mt-2">{item1.notes}</p>}
                            {item1.photos.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-2">
                                {item1.photos.map((photo, i) => (
                                  <img
                                    key={i}
                                    src={photo}
                                    alt=""
                                    className="w-20 h-20 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                                    onClick={() => handlePhotoClick(photo, item1.name, room1.roomName, item1.photos, 'PRZED')}
                                  />
                                ))}
                              </div>
                            )}
                          </div>

                          <div>
                            <div className="text-xs text-emerald-600 font-medium mb-2">PO</div>
                            <div
                              className="px-3 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-1"
                              style={{
                                backgroundColor: CONDITIONS[item2.condition].color + '15',
                                color: CONDITIONS[item2.condition].color,
                              }}
                            >
                              {React.createElement(CONDITIONS[item2.condition].icon, { className: 'w-4 h-4' })}
                              {CONDITIONS[item2.condition].label}
                            </div>
                            {item2.notes && <p className="text-sm text-slate-600 mt-2">{item2.notes}</p>}
                            {item2.photos.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-2">
                                {item2.photos.map((photo, i) => (
                                  <img
                                    key={i}
                                    src={photo}
                                    alt=""
                                    className="w-20 h-20 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                                    onClick={() => handlePhotoClick(photo, item2.name, room2.roomName, item2.photos, 'PO')}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Photo Modal */}
      <PhotoModal
        isOpen={photoModal.isOpen}
        photoUrl={photoModal.photoUrl}
        caption={photoModal.caption}
        allPhotos={photoModal.allPhotos}
        currentIndex={photoModal.currentIndex}
        onNavigate={handlePhotoNavigate}
        onClose={() => setPhotoModal({ ...photoModal, isOpen: false })}
      />
    </>
  );
}
